import { useState } from 'react'
import { cn } from '@/lib/utils'
import { IntuiButton, IntuiButtonGroup } from '@/components/intueri/IntuiButton'
import { IntuiTypography } from '@/components/intueri/IntuiTypography'
import { IntuiTable, type IntuiColumnDef } from '@/components/intueri/IntuiTable'
import { IntuiBreadcrumb } from '@/components/intueri/IntuiBreadcrumb'
import { IntuiDrawer } from '@/components/intueri/IntuiDrawer'
import { IntuiAspectRatio } from '@/components/intueri/IntuiAspectRatio'
import { IntuiBadge } from '@/components/intueri/IntuiBadge'
import { IntuiSeparator } from '@/components/intueri/IntuiSeparator'
import { IntuiSpinner } from '@/components/intueri/IntuiSpinner'
import { IntuiSwitch } from '@/components/intueri/IntuiSwitch'
import { IntuiToggle } from '@/components/intueri/IntuiToggle'
import { IntuiCheckbox } from '@/components/intueri/IntuiCheckbox'
import { IntuiLabel } from '@/components/intueri/IntuiLabel'
import { IntuiProgress } from '@/components/intueri/IntuiProgress'
import { IntuiSkeleton } from '@/components/intueri/IntuiSkeleton'
import { IntuiKbd, IntuiKbdGroup } from '@/components/intueri/IntuiKbd'
import { IntuiTooltip } from '@/components/intueri/IntuiTooltip'
import { IntuiAlert } from '@/components/intueri/IntuiAlert'
import { IntuiAccordion } from '@/components/intueri/IntuiAccordion'
import { IntuiAvatar, IntuiAvatarGroup } from '@/components/intueri/IntuiAvatar'
import { IntuiDialog, IntuiDialogClose } from '@/components/intueri/IntuiDialog'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import {
  IntuiInputGroup,
  IntuiInputGroupInput,
  IntuiInputGroupTextarea,
  IntuiInputGroupText,
} from '@/components/intueri/IntuiInputGroup'
import {
  IntuiField,
  IntuiFieldGroup,
  IntuiFieldLegend,
  IntuiFieldSeparator,
  IntuiFieldButtons,
} from '@/components/intueri/IntuiField'
import { IntuiSlider } from '@/components/intueri/IntuiSlider'
import { IntuiTabs, IntuiTabsList, IntuiTabsTrigger, IntuiTabsContent } from '@/components/intueri/IntuiTabs'
import { IntuiCalendar, IntuiCalendarPresets, IntuiCalendarDateTime } from '@/components/intueri/IntuiCalendar'
import {
  IntuiSidebar,
  IntuiSidebarHeader,
  IntuiSidebarContent,
  IntuiSidebarFooter,
  IntuiSidebarGroup,
  IntuiSidebarGroupLabel,
  IntuiSidebarMenu,
  IntuiSidebarMenuItem,
  IntuiSidebarMenuButton,
  IntuiSidebarHeaderButton,
  IntuiSidebarMenuSub,
  IntuiSidebarMenuSubItem,
  IntuiSidebarGroupAction,
  IntuiSidebarInboxItem,
} from '@/components/intueri/IntuiSidebar'
import {
  IntuiMenubar,
  IntuiMenubarMenu,
  IntuiMenubarTrigger,
  IntuiMenubarContent,
  IntuiMenubarItem,
  IntuiMenubarSeparator,
  IntuiMenubarCheckboxItem,
  IntuiMenubarRadioGroup,
  IntuiMenubarRadioItem,
  IntuiMenubarSub,
  IntuiMenubarSubTrigger,
  IntuiMenubarSubContent,
  IntuiMenubarLabel,
} from '@/components/intueri/IntuiMenubar'
import {
  IntuiNavigationMenu,
  IntuiNavigationMenuList,
  IntuiNavigationMenuItem,
  IntuiNavigationMenuTrigger,
  IntuiNavigationMenuContent,
  IntuiNavigationMenuLink,
  IntuiNavigationMenuListItem,
  navigationMenuTriggerStyle,
} from '@/components/intueri/IntuiNavigationMenu'
import {
  IntuiSelect,
  IntuiSelectTrigger,
  IntuiSelectValue,
  IntuiSelectContent,
  IntuiSelectItem,
  IntuiSelectSeparator,
} from '@/components/intueri/IntuiSelect'
import {
  IntuiCombobox,
  IntuiComboboxTrigger,
  IntuiComboboxContent,
  IntuiComboboxInput,
  IntuiComboboxList,
  IntuiComboboxEmpty,
  IntuiComboboxItem,
} from '@/components/intueri/IntuiCombobox'
import {
  IntuiAlertDialog,
  IntuiAlertDialogTrigger,
  IntuiAlertDialogContent,
  IntuiAlertDialogHeader,
  IntuiAlertDialogTitle,
  IntuiAlertDialogDescription,
  IntuiAlertDialogFooter,
  IntuiAlertDialogAction,
  IntuiAlertDialogCancel,
} from '@/components/intueri/IntuiAlertDialog'
import {
  IntuiCard,
  IntuiCardHeader,
  IntuiCardTitle,
  IntuiCardDescription,
  IntuiCardContent,
  IntuiCardFooter,
} from '@/components/intueri/IntuiCard'
import { IntuiCollapsible, IntuiCollapsibleTrigger, IntuiCollapsibleContent } from '@/components/intueri/IntuiCollapsible'
import { IntuiHoverCard, IntuiHoverCardTrigger, IntuiHoverCardContent } from '@/components/intueri/IntuiHoverCard'
import { IntuiPopover, IntuiPopoverTrigger, IntuiPopoverContent } from '@/components/intueri/IntuiPopover'
import { IntuiRadioGroup, IntuiRadioGroupItem } from '@/components/intueri/IntuiRadioGroup'
import { IntuiScrollArea } from '@/components/intueri/IntuiScrollArea'
import { IntuiToaster, toast } from '@/components/intueri/IntuiToast'
import { IntuiTextarea } from '@/components/intueri/IntuiTextarea'
import { IntuiPagination } from '@/components/intueri/IntuiPagination'
import { IntuiEmptyState } from '@/components/intueri/IntuiEmptyState'
import { IntuiDirectionProvider } from '@/components/intueri/IntuiDirection'
import { IntuiItem, IntuiItemGroup } from '@/components/intueri/IntuiItem'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  IntuiInputOTP,
  IntuiInputOTPGroup,
  IntuiInputOTPSlot,
  IntuiInputOTPSpacedSlot,
  IntuiInputOTPSeparator,
  REGEXP_ONLY_DIGITS,
} from '@/components/intueri/IntuiInputOTP'
import {
  IntuiResizablePanelGroup,
  IntuiResizablePanel,
  IntuiResizableHandle,
} from '@/components/intueri/IntuiResizable'
import {
  IntuiCommand,
  IntuiCommandInput,
  IntuiCommandList,
  IntuiCommandEmpty,
  IntuiCommandGroup,
  IntuiCommandItem,
  IntuiCommandSeparator,
} from '@/components/intueri/IntuiCommand'
import { IntuiAreaChart, IntuiChartCard, type ChartConfig } from '@/components/intueri/IntuiAreaChart'
import { IntuiBarChart } from '@/components/intueri/IntuiBarChart'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import {
  IntuiCarousel,
  IntuiCarouselContent,
  IntuiCarouselItem,
  IntuiCarouselPrevious,
  IntuiCarouselNext,
  useCarouselCounter,
} from '@/components/intueri/IntuiCarousel'
import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts'
import { IntuiLineChart } from '@/components/intueri/IntuiLineChart'
import { IntuiPieChart } from '@/components/intueri/IntuiPieChart'
import { IntuiRadarChart } from '@/components/intueri/IntuiRadarChart'
import { IntuiRadialChart } from '@/components/intueri/IntuiRadialChart'
import {
  IntuiContextMenu,
  IntuiContextMenuTrigger,
  IntuiContextMenuContent,
  IntuiContextMenuItem,
  IntuiContextMenuLabel,
  IntuiContextMenuSeparator,
  IntuiContextMenuSub,
  IntuiContextMenuSubTrigger,
  IntuiContextMenuSubContent,
  IntuiContextMenuCheckboxItem,
  IntuiContextMenuRadioGroup,
  IntuiContextMenuRadioItem,
} from '@/components/intueri/IntuiContextMenu'
import {
  IntuiDatePicker,
  IntuiDateRangePicker,
  IntuiTimePicker,
  IntuiDateTimePicker,
} from '@/components/intueri/IntuiDatePicker'
import {
  IntuiDropdownMenu,
  IntuiDropdownMenuTrigger,
  IntuiDropdownMenuContent,
  IntuiDropdownMenuItem,
  IntuiDropdownMenuLabel,
  IntuiDropdownMenuSeparator,
  IntuiDropdownMenuSub,
  IntuiDropdownMenuSubTrigger,
  IntuiDropdownMenuSubContent,
  IntuiDropdownMenuCheckboxItem,
  IntuiDropdownMenuRadioGroup,
  IntuiDropdownMenuRadioItem,
  IntuiDropdownMenuGroup,
} from '@/components/intueri/IntuiDropdownMenu'
import {
  Bold,
  Italic,
  User,
  Settings,
  LogOut,
  Trash2,
  Copy,
  Scissors,
  Clipboard,
  Mail,
  MessageSquare,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Bell,
  FileText,
  FolderPlus,
  FolderOpen,
  Save,
  Download,
  Eye,
  EyeOff,
  Palette,
  HelpCircle,
  BookOpen,
  Globe,
  Search,
  Star,
  Loader2,
  Bookmark,
  Check,
  ArrowRight,
  Plus,
  ChevronRight,
  ChevronsUpDown,
  ChevronDown,
  Minus,
  MoreHorizontal,
  Send,
  Trash2 as TrashIcon,
  Archive,
  Sparkles,
  Hash,
  SquareTerminal,
  Bot,
  LayoutDashboard,
  Folder,
  File,
  CheckSquare,
  MessageCircle,
  Paintbrush,
  Languages,
  Accessibility,
  Monitor,
  Lock,
  Link,
  Pencil,
  Share2,
  Calendar,
  Smile,
  Calculator,
  Home,
  Inbox,
  CalendarDays,
  Image,
  Music,
  Video,
} from 'lucide-react'

// Sample data for IntuiTable demo (module scope to avoid per-render recreation)
type InvoiceRow = { id: string; invoice: string; status: string; method: string; amount: string }

const SAMPLE_TABLE_COLUMNS: IntuiColumnDef<InvoiceRow>[] = [
  { key: 'invoice', header: 'Invoice', cell: (row) => <span className="font-medium">{row.invoice}</span>, sortable: true, comparator: (a, b) => a.invoice.localeCompare(b.invoice) },
  { key: 'status', header: 'Status', cell: (row) => row.status, sortable: true, comparator: (a, b) => a.status.localeCompare(b.status) },
  { key: 'method', header: 'Method', cell: (row) => row.method },
  { key: 'amount', header: 'Amount', cell: (row) => row.amount, align: 'right' as const },
]

const SAMPLE_TABLE_DATA: InvoiceRow[] = [
  { id: '1', invoice: 'INV001', status: 'Paid', method: 'Credit Card', amount: '$120.00' },
  { id: '2', invoice: 'INV002', status: 'Pending', method: 'PayPal', amount: '$340.00' },
  { id: '3', invoice: 'INV003', status: 'Paid', method: 'Credit Card', amount: '$110.00' },
  { id: '4', invoice: 'INV004', status: 'Paid', method: 'Credit Card', amount: '$240.00' },
  { id: '5', invoice: 'INV005', status: 'Unpaid', method: 'Bank Transfer', amount: '$330.00' },
]

// Sample data for Area Chart demos
const AREA_CHART_DATA = [
  { month: 'January', desktop: 186, mobile: 80 },
  { month: 'February', desktop: 305, mobile: 200 },
  { month: 'March', desktop: 237, mobile: 120 },
  { month: 'April', desktop: 73, mobile: 190 },
  { month: 'May', desktop: 209, mobile: 130 },
  { month: 'June', desktop: 214, mobile: 140 },
]

const AREA_CHART_SINGLE_CONFIG: ChartConfig = {
  desktop: { label: 'Desktop', color: 'hsl(var(--chart-1))' },
}

const AREA_CHART_MULTI_CONFIG: ChartConfig = {
  desktop: { label: 'Desktop', color: 'hsl(var(--chart-1))' },
  mobile: { label: 'Mobile', color: 'hsl(var(--chart-2))' },
}

const AREA_CHART_ICON_CONFIG: ChartConfig = {
  desktop: { label: 'Desktop', color: 'hsl(var(--chart-1))', icon: Globe },
  mobile: { label: 'Mobile', color: 'hsl(var(--chart-2))', icon: Mail },
}

// Sample data for Bar Chart demos (reuses AREA_CHART_DATA for basic demos)
const BAR_CHART_MIXED_DATA = [
  { browser: 'Chrome', visitors: 275, fill: 'hsl(var(--chart-1))' },
  { browser: 'Safari', visitors: 200, fill: 'hsl(var(--chart-2))' },
  { browser: 'Firefox', visitors: 187, fill: 'hsl(var(--chart-3))' },
  { browser: 'Edge', visitors: 173, fill: 'hsl(var(--chart-4))' },
  { browser: 'Other', visitors: 90, fill: 'hsl(var(--chart-5))' },
]

const BAR_CHART_MIXED_CONFIG: ChartConfig = {
  visitors: { label: 'Visitors', color: 'hsl(var(--chart-1))' },
  chrome: { label: 'Chrome', color: 'hsl(var(--chart-1))' },
  safari: { label: 'Safari', color: 'hsl(var(--chart-2))' },
  firefox: { label: 'Firefox', color: 'hsl(var(--chart-3))' },
  edge: { label: 'Edge', color: 'hsl(var(--chart-4))' },
  other: { label: 'Other', color: 'hsl(var(--chart-5))' },
}

const BAR_CHART_NEGATIVE_DATA = [
  { month: 'January', desktop: 186 },
  { month: 'February', desktop: -305 },
  { month: 'March', desktop: 237 },
  { month: 'April', desktop: -73 },
  { month: 'May', desktop: 209 },
  { month: 'June', desktop: -140 },
]

// Sample data for Pie Chart demos
const PIE_CHART_DATA = [
  { browser: 'Chrome', visitors: 275, fill: 'hsl(var(--chart-1))' },
  { browser: 'Safari', visitors: 200, fill: 'hsl(var(--chart-2))' },
  { browser: 'Firefox', visitors: 187, fill: 'hsl(var(--chart-3))' },
  { browser: 'Edge', visitors: 173, fill: 'hsl(var(--chart-4))' },
  { browser: 'Other', visitors: 90, fill: 'hsl(var(--chart-5))' },
]

const PIE_CHART_CONFIG: ChartConfig = {
  visitors: { label: 'Visitors' },
  chrome: { label: 'Chrome', color: 'hsl(var(--chart-1))' },
  safari: { label: 'Safari', color: 'hsl(var(--chart-2))' },
  firefox: { label: 'Firefox', color: 'hsl(var(--chart-3))' },
  edge: { label: 'Edge', color: 'hsl(var(--chart-4))' },
  other: { label: 'Other', color: 'hsl(var(--chart-5))' },
}

// Radial chart single-item data
const RADIAL_SINGLE_DATA = [
  { browser: 'Safari', visitors: 200, fill: 'hsl(var(--chart-2))' },
]

function CarouselWithCounter() {
  const { current, count, setApi } = useCarouselCounter()
  return (
    <div className="flex flex-col items-center gap-2">
      <IntuiCarousel setApi={setApi} className="w-full max-w-xs">
        <IntuiCarouselContent>
          {Array.from({ length: 5 }).map((_, i) => (
            <IntuiCarouselItem key={i}>
              <div className="p-1">
                <div className="flex aspect-square items-center justify-center rounded-lg border bg-card">
                  <span className="text-3xl font-semibold">{i + 1}</span>
                </div>
              </div>
            </IntuiCarouselItem>
          ))}
        </IntuiCarouselContent>
        <IntuiCarouselPrevious />
        <IntuiCarouselNext />
      </IntuiCarousel>
      <p className="text-sm text-muted-foreground">
        Slide {current} of {count}
      </p>
    </div>
  )
}

// Calendar demo constants
const CALENDAR_DROPDOWN_START = new Date(2020, 0)
const CALENDAR_DROPDOWN_END = new Date(2030, 11)

function CalendarDateTimeDemo() {
  const [startTime, setStartTime] = useState('10:30:00')
  const [endTime, setEndTime] = useState('10:30:00')
  return (
    <IntuiCalendarDateTime
      startTime={startTime}
      endTime={endTime}
      onStartTimeChange={setStartTime}
      onEndTimeChange={setEndTime}
    />
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-foreground border-b pb-2">{title}</h2>
      <div className="space-y-6 pl-2">{children}</div>
    </div>
  )
}

function Subsection({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <div className="flex flex-wrap items-start gap-4">{children}</div>
    </div>
  )
}

export default function RevampComponents() {
  const [switchChecked, setSwitchChecked] = useState(false)
  const [checkboxChecked, setCheckboxChecked] = useState<boolean | 'indeterminate'>(false)
  const [showBookmarks, setShowBookmarks] = useState(true)
  const [showUrls, setShowUrls] = useState(false)
  const [radioValue, setRadioValue] = useState('pedro')
  const [emailNotif, setEmailNotif] = useState(true)
  const [smsNotif, setSmsNotif] = useState(false)
  const [pushNotif, setPushNotif] = useState(true)
  const [showSidebar, setShowSidebar] = useState(true)
  const [showStatusBar, setShowStatusBar] = useState(false)
  const [bookmarked, setBookmarked] = useState(false)
  const [socialLink] = useState('https://x.com/shadcn')
  const [socialCopied, setSocialCopied] = useState(false)
  const [codeCopied, setCodeCopied] = useState(false)
  const [autosizeValue, setAutosizeValue] = useState('')
  const [codeValue, setCodeValue] = useState("console.log('Hello, world!');")
  const [commentValue, setCommentValue] = useState('')
  const [messageValue, setMessageValue] = useState('')
  const COMMENT_MAX = 280
  const MESSAGE_MAX = 280
  const [sliderValue, setSliderValue] = useState([200, 800])
  const [hardDisks, setHardDisks] = useState<boolean | 'indeterminate'>(true)
  const [externalDisks, setExternalDisks] = useState<boolean | 'indeterminate'>(false)
  const [cds, setCds] = useState<boolean | 'indeterminate'>(false)
  const [connectedServers, setConnectedServers] = useState<boolean | 'indeterminate'>(false)
  const [subscriptionPlan, setSubscriptionPlan] = useState('monthly')
  const [paginationPage, setPaginationPage] = useState(1)
  const [comboboxOpen, setComboboxOpen] = useState(false)
  const [comboboxValue, setComboboxValue] = useState('')
  const [collapsibleOpen, setCollapsibleOpen] = useState(false)
  const [textareaValue, setTextareaValue] = useState('')
  const [intuiRadioValue, setIntuiRadioValue] = useState('comfortable')
  const [mfaEnabled, setMfaEnabled] = useState(false)
  const [computeEnv, setComputeEnv] = useState('kubernetes')
  const [sameAsShipping, setSameAsShipping] = useState<boolean | 'indeterminate'>(true)
  const [alertDismissed, setAlertDismissed] = useState(false)
  const [togglePressed, setTogglePressed] = useState(false)
  const [progressValue, setProgressValue] = useState(45)

  return (
    <div className="min-h-screen bg-background text-foreground p-8 max-w-5xl mx-auto space-y-12">
      <div>
        <h1 className="text-3xl font-bold">Intui Component Library</h1>
        <p className="text-muted-foreground mt-1">
          Dev-only preview of all Intueri design system components
        </p>
      </div>

      {/* All components rendered directly (single page) */}
      {/* Typography */}
      <Section title="Typography">
        <div className="space-y-2">
          <IntuiTypography variant="h1">Heading 1</IntuiTypography>
          <IntuiTypography variant="h2">Heading 2</IntuiTypography>
          <IntuiTypography variant="h3">Heading 3</IntuiTypography>
          <IntuiTypography variant="h4">Heading 4</IntuiTypography>
          <IntuiTypography variant="p">Paragraph text — The quick brown fox jumps over the lazy dog.</IntuiTypography>
          <IntuiTypography variant="muted">Muted text for secondary information</IntuiTypography>
          <IntuiTypography variant="small">Small text for fine print</IntuiTypography>
        </div>
      </Section>

      <IntuiSeparator />

      {/* Button */}
      <Section title="Button">
        <Subsection label="Variants">
          <IntuiButton>Default</IntuiButton>
          <IntuiButton variant="secondary">Secondary</IntuiButton>
          <IntuiButton variant="destructive">Destructive</IntuiButton>
          <IntuiButton variant="outline">Outline</IntuiButton>
          <IntuiButton variant="ghost">Ghost</IntuiButton>
          <IntuiButton variant="link">Link</IntuiButton>
        </Subsection>

        <Subsection label="Sizes">
          <IntuiButton size="xs">Extra Small</IntuiButton>
          <IntuiButton size="sm">Small</IntuiButton>
          <IntuiButton>Default</IntuiButton>
          <IntuiButton size="lg">Large</IntuiButton>
        </Subsection>

        <Subsection label="Icon buttons">
          <IntuiButton size="icon-xs"><Plus /></IntuiButton>
          <IntuiButton size="icon-sm"><Plus /></IntuiButton>
          <IntuiButton size="icon"><Plus /></IntuiButton>
          <IntuiButton size="icon-lg"><Plus /></IntuiButton>
          <IntuiButton variant="secondary" size="icon"><Settings /></IntuiButton>
          <IntuiButton variant="outline" size="icon"><Search /></IntuiButton>
          <IntuiButton variant="ghost" size="icon"><Mail /></IntuiButton>
          <IntuiButton variant="destructive" size="icon"><Trash2 /></IntuiButton>
        </Subsection>

        <Subsection label="With icons">
          <IntuiButton leftIcon={<Mail />}>Login with Email</IntuiButton>
          <IntuiButton rightIcon={<ArrowRight />}>Next</IntuiButton>
          <IntuiButton leftIcon={<Mail />} rightIcon={<ChevronRight />}>Send Email</IntuiButton>
          <IntuiButton variant="outline" leftIcon={<Mail />}>Email</IntuiButton>
          <IntuiButton variant="secondary" leftIcon={<Plus />}>New Item</IntuiButton>
        </Subsection>

        <Subsection label="Loading">
          <IntuiButton loading>Default</IntuiButton>
          <IntuiButton loading variant="secondary">Secondary</IntuiButton>
          <IntuiButton loading variant="destructive">Destructive</IntuiButton>
          <IntuiButton loading variant="outline">Outline</IntuiButton>
          <IntuiButton loading size="icon" aria-label="Loading" />
        </Subsection>

        <Subsection label="Disabled">
          <IntuiButton disabled>Default</IntuiButton>
          <IntuiButton disabled variant="secondary">Secondary</IntuiButton>
          <IntuiButton disabled variant="destructive">Destructive</IntuiButton>
          <IntuiButton disabled variant="outline">Outline</IntuiButton>
          <IntuiButton disabled variant="ghost">Ghost</IntuiButton>
          <IntuiButton disabled variant="link">Link</IntuiButton>
        </Subsection>

        <Subsection label="All variants × sizes">
          <div className="space-y-4">
            {(['default', 'secondary', 'destructive', 'outline', 'ghost', 'link'] as const).map((v) => (
              <div key={v} className="flex items-center gap-2">
                <span className="w-24 text-xs text-muted-foreground capitalize">{v}</span>
                <IntuiButton variant={v} size="xs">Button</IntuiButton>
                <IntuiButton variant={v} size="sm">Button</IntuiButton>
                <IntuiButton variant={v}>Button</IntuiButton>
                <IntuiButton variant={v} size="lg">Button</IntuiButton>
              </div>
            ))}
          </div>
        </Subsection>

        <Subsection label="Loading with right icon (Figma: 'Please wait')">
          <IntuiButton loading rightIcon={<ArrowRight />}>Please wait</IntuiButton>
          <IntuiButton loading rightIcon={<ArrowRight />} variant="secondary">Please wait</IntuiButton>
          <IntuiButton loading rightIcon={<ArrowRight />} variant="outline">Please wait</IntuiButton>
        </Subsection>

        <Subsection label="Common patterns (Figma playground)">
          <IntuiButton leftIcon={<Mail />}>Send</IntuiButton>
          <IntuiButton rightIcon={<ArrowRight />}>Learn more</IntuiButton>
          <div className="flex gap-1">
            <IntuiButton>Get Started</IntuiButton>
            <IntuiButton size="icon" variant="outline"><ArrowRight /></IntuiButton>
          </div>
        </Subsection>

        <Subsection label="Figma playground — xs size group">
          <IntuiButton size="xs">Button</IntuiButton>
          <IntuiButton size="xs" variant="outline">Outline</IntuiButton>
          <IntuiButton size="xs" variant="ghost">Ghost</IntuiButton>
          <IntuiButton size="xs" variant="destructive">Destructive</IntuiButton>
          <IntuiButton size="xs" variant="secondary">Secondary</IntuiButton>
          <IntuiButton size="xs" variant="link">Link</IntuiButton>
          <IntuiButton size="xs" leftIcon={<Mail />}>Send</IntuiButton>
          <IntuiButton size="xs" rightIcon={<ArrowRight />}>Learn more</IntuiButton>
          <IntuiButton size="xs" loading rightIcon={<ArrowRight />}>Please wait</IntuiButton>
        </Subsection>

        <Subsection label="Figma playground — default size group">
          <IntuiButton>Button</IntuiButton>
          <IntuiButton variant="outline">Outline</IntuiButton>
          <IntuiButton variant="ghost">Ghost</IntuiButton>
          <IntuiButton variant="destructive">Destructive</IntuiButton>
          <IntuiButton variant="secondary">Secondary</IntuiButton>
          <IntuiButton variant="link">Link</IntuiButton>
          <IntuiButton leftIcon={<Mail />}>Send</IntuiButton>
          <IntuiButton rightIcon={<ArrowRight />}>Learn more</IntuiButton>
          <IntuiButton loading rightIcon={<ArrowRight />}>Please wait</IntuiButton>
        </Subsection>

        <Subsection label="Figma playground — lg size group">
          <IntuiButton size="lg">Large</IntuiButton>
          <IntuiButton size="lg" variant="outline">Outline</IntuiButton>
          <IntuiButton size="lg" variant="ghost">Ghost</IntuiButton>
          <IntuiButton size="lg" variant="destructive">Destructive</IntuiButton>
          <IntuiButton size="lg" variant="secondary">Secondary</IntuiButton>
          <IntuiButton size="lg" variant="link">Link</IntuiButton>
          <IntuiButton size="lg" leftIcon={<Mail />}>Send</IntuiButton>
          <IntuiButton size="lg" rightIcon={<ArrowRight />}>Learn more</IntuiButton>
          <IntuiButton size="lg" loading rightIcon={<ArrowRight />}>Please wait</IntuiButton>
          <div className="flex gap-1">
            <IntuiButton size="lg">Get Started</IntuiButton>
            <IntuiButton size="icon-lg" variant="outline"><ArrowRight /></IntuiButton>
          </div>
        </Subsection>

        <Subsection label="As link (asChild)">
          <IntuiButton asChild>
            <a href="#button-demo">Link as Button</a>
          </IntuiButton>
          <IntuiButton variant="outline" asChild>
            <a href="#button-demo">Outline Link</a>
          </IntuiButton>
        </Subsection>

        <Subsection label="Button Group — Default (horizontal)">
          <IntuiButtonGroup variant="default">
            <IntuiButton>Button</IntuiButton>
            <IntuiButton>Button</IntuiButton>
            <IntuiButton>Button</IntuiButton>
            <IntuiButton>Button</IntuiButton>
            <IntuiButton>Button</IntuiButton>
            <IntuiButton size="icon"><ChevronRight /></IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Button Group — Outline (horizontal)">
          <IntuiButtonGroup variant="outline">
            <IntuiButton variant="outline">Button</IntuiButton>
            <IntuiButton variant="outline">Button</IntuiButton>
            <IntuiButton variant="outline">Button</IntuiButton>
            <IntuiButton variant="outline">Button</IntuiButton>
            <IntuiButton variant="outline">Button</IntuiButton>
            <IntuiButton variant="outline" size="icon"><ChevronRight /></IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Button Group — Secondary (horizontal)">
          <IntuiButtonGroup variant="secondary">
            <IntuiButton variant="secondary">Button</IntuiButton>
            <IntuiButton variant="secondary">Button</IntuiButton>
            <IntuiButton variant="secondary">Button</IntuiButton>
            <IntuiButton variant="secondary">Button</IntuiButton>
            <IntuiButton variant="secondary">Button</IntuiButton>
            <IntuiButton variant="secondary" size="icon"><ChevronRight /></IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Button Group — Vertical">
          <div className="flex gap-8">
            <IntuiButtonGroup variant="default" orientation="vertical">
              <IntuiButton>Button</IntuiButton>
              <IntuiButton>Button</IntuiButton>
              <IntuiButton>Button</IntuiButton>
              <IntuiButton>Button</IntuiButton>
              <IntuiButton>Button</IntuiButton>
              <IntuiButton>Button</IntuiButton>
            </IntuiButtonGroup>
            <IntuiButtonGroup variant="outline" orientation="vertical">
              <IntuiButton variant="outline">Button</IntuiButton>
              <IntuiButton variant="outline">Button</IntuiButton>
              <IntuiButton variant="outline">Button</IntuiButton>
              <IntuiButton variant="outline">Button</IntuiButton>
              <IntuiButton variant="outline">Button</IntuiButton>
              <IntuiButton variant="outline">Button</IntuiButton>
            </IntuiButtonGroup>
            <IntuiButtonGroup variant="secondary" orientation="vertical">
              <IntuiButton variant="secondary">Button</IntuiButton>
              <IntuiButton variant="secondary">Button</IntuiButton>
              <IntuiButton variant="secondary">Button</IntuiButton>
              <IntuiButton variant="secondary">Button</IntuiButton>
              <IntuiButton variant="secondary">Button</IntuiButton>
              <IntuiButton variant="secondary">Button</IntuiButton>
            </IntuiButtonGroup>
          </div>
        </Subsection>

        <Subsection label="Email toolbar (icon + text buttons + icon)">
          <IntuiButtonGroup variant="outline">
            <IntuiButton variant="outline" size="icon"><ArrowRight className="rotate-180" /></IntuiButton>
            <IntuiButton variant="outline">Archive</IntuiButton>
            <IntuiButton variant="outline">Report</IntuiButton>
            <IntuiButton variant="outline">Snooze</IntuiButton>
            <IntuiButton variant="outline" size="icon"><Settings /></IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Size variants (sm, default, lg)">
          <div className="space-y-3">
            <IntuiButtonGroup variant="outline">
              <IntuiButton variant="outline" size="sm">Undo</IntuiButton>
              <IntuiButton variant="outline" size="sm">Redo</IntuiButton>
              <IntuiButton variant="outline" size="icon-sm"><ChevronRight /></IntuiButton>
            </IntuiButtonGroup>
            <IntuiButtonGroup variant="outline">
              <IntuiButton variant="outline">Default</IntuiButton>
              <IntuiButton variant="outline">Button</IntuiButton>
              <IntuiButton variant="outline">Group</IntuiButton>
              <IntuiButton variant="outline" size="icon"><ChevronRight /></IntuiButton>
            </IntuiButtonGroup>
            <IntuiButtonGroup variant="outline">
              <IntuiButton variant="outline" size="lg">Large</IntuiButton>
              <IntuiButton variant="outline" size="lg">Button</IntuiButton>
              <IntuiButton variant="outline" size="lg">Group</IntuiButton>
              <IntuiButton variant="outline" size="icon-lg"><ChevronRight /></IntuiButton>
            </IntuiButtonGroup>
          </div>
        </Subsection>

        <Subsection label="Button + Input combo">
          <div className="space-y-3">
            <div className="flex">
              <IntuiButton variant="outline" className="rounded-r-none border-r-0">Search...</IntuiButton>
              <IntuiButton variant="outline" size="icon" className="rounded-l-none"><Search /></IntuiButton>
            </div>
            <div className="flex">
              <IntuiButton variant="outline" className="rounded-r-none">Button</IntuiButton>
              <input className="flex-1 h-8 rounded-none border border-l-0 border-input bg-background px-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" placeholder="Type something" />
              <IntuiButton variant="outline" size="icon" className="rounded-l-none border-l-0"><Search /></IntuiButton>
            </div>
          </div>
        </Subsection>

        <Subsection label="Split button with dropdown trigger">
          <div className="flex">
            <IntuiButton className="rounded-r-none">Follow</IntuiButton>
            <IntuiButton size="icon" className="rounded-l-none border-l border-l-primary-foreground/20"><ChevronRight className="rotate-90" /></IntuiButton>
          </div>
        </Subsection>

        <Subsection label="Button + Get Started pair">
          <IntuiButtonGroup variant="default">
            <IntuiButton>Button</IntuiButton>
            <IntuiButton rightIcon={<ArrowRight />}>Get Started</IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Outline Button + Another Button">
          <IntuiButtonGroup variant="outline">
            <IntuiButton variant="outline">Button</IntuiButton>
            <IntuiButton variant="outline">Another Button</IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Vertical 2-button">
          <IntuiButtonGroup variant="outline" orientation="vertical">
            <IntuiButton variant="outline" size="icon"><Plus /></IntuiButton>
            <IntuiButton variant="outline" size="icon"><ArrowRight className="rotate-180" /></IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Send message input">
          <div className="flex">
            <IntuiButton variant="outline" size="icon" className="rounded-r-none border-r-0"><Mail /></IntuiButton>
            <input className="flex-1 h-8 border border-input bg-background px-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" placeholder="Send a message..." />
            <IntuiButton variant="outline" size="icon" className="rounded-l-none border-l-0"><Search /></IntuiButton>
          </div>
        </Subsection>

        <Subsection label="Button + Get Started (outline)">
          <IntuiButtonGroup variant="outline">
            <IntuiButton variant="outline">Button</IntuiButton>
            <IntuiButton variant="outline" rightIcon={<ChevronRight />}>Get Started</IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Button + Input + Button">
          <div className="flex">
            <IntuiButton variant="outline" className="rounded-r-none border-r-0">Button</IntuiButton>
            <input className="flex-1 h-8 border border-input bg-background px-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none" placeholder="Type something" />
            <IntuiButton variant="outline" className="rounded-l-none border-l-0">Button</IntuiButton>
          </div>
        </Subsection>

        <Subsection label="URL input combo">
          <div className="flex">
            <IntuiButton variant="outline" size="icon" className="rounded-r-none border-r-0"><Globe /></IntuiButton>
            <input className="flex-1 h-8 border border-input bg-background px-2.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none" placeholder="Type something" />
            <IntuiButton variant="outline" className="rounded-l-none border-l-0">.pdf</IntuiButton>
          </div>
        </Subsection>

        <Subsection label="Text alignment toolbar">
          <IntuiButtonGroup variant="outline">
            <IntuiButton variant="outline" size="icon"><Bold /></IntuiButton>
            <IntuiButton variant="outline" size="icon"><Italic /></IntuiButton>
            <IntuiButton variant="outline" size="icon"><Settings /></IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Pagination">
          <IntuiButtonGroup variant="outline">
            <IntuiButton variant="outline" size="icon-sm"><ArrowRight className="rotate-180" /></IntuiButton>
            <IntuiButton variant="outline" size="sm">1</IntuiButton>
            <IntuiButton variant="outline" size="sm">2</IntuiButton>
            <IntuiButton variant="outline" size="sm">3</IntuiButton>
            <IntuiButton variant="outline" size="sm">...</IntuiButton>
            <IntuiButton variant="outline" size="sm">10</IntuiButton>
            <IntuiButton variant="outline" size="icon-sm"><ArrowRight /></IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Text alignment (Left/Center/Right/Justify)">
          <IntuiButtonGroup variant="outline">
            <IntuiButton variant="outline" size="sm">Left</IntuiButton>
            <IntuiButton variant="outline" size="sm">Center</IntuiButton>
            <IntuiButton variant="outline" size="sm">Right</IntuiButton>
            <IntuiButton variant="outline" size="sm">Justify</IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Toggle group (Monthly/Annually)">
          <IntuiButtonGroup variant="outline">
            <IntuiButton variant="outline" size="sm">Monthly</IntuiButton>
            <IntuiButton variant="outline" size="sm">Annually</IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Vertical icon toolbar">
          <IntuiButtonGroup variant="outline" orientation="vertical">
            <IntuiButton variant="outline" size="icon"><Search /></IntuiButton>
            <IntuiButton variant="outline" size="icon"><Copy /></IntuiButton>
            <IntuiButton variant="outline" size="icon"><Download /></IntuiButton>
            <IntuiButton variant="outline" size="icon"><Clipboard /></IntuiButton>
            <IntuiButton variant="outline" size="icon"><Save /></IntuiButton>
            <IntuiButton variant="outline" size="icon"><Settings /></IntuiButton>
          </IntuiButtonGroup>
        </Subsection>

        <Subsection label="Vertical number list">
          <IntuiButtonGroup variant="outline" orientation="vertical">
            <IntuiButton variant="outline" size="icon">1</IntuiButton>
            <IntuiButton variant="outline" size="icon">2</IntuiButton>
            <IntuiButton variant="outline" size="icon">3</IntuiButton>
            <IntuiButton variant="outline" size="icon">4</IntuiButton>
            <IntuiButton variant="outline" size="icon">5</IntuiButton>
            <IntuiButton variant="outline" size="icon">6</IntuiButton>
          </IntuiButtonGroup>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Badge */}
      <Section title="Badge">
        <Subsection label="Variants">
          <IntuiBadge>Default</IntuiBadge>
          <IntuiBadge variant="secondary">Secondary</IntuiBadge>
          <IntuiBadge variant="destructive">Destructive</IntuiBadge>
          <IntuiBadge variant="outline">Outline</IntuiBadge>
          <IntuiBadge variant="ghost">Ghost</IntuiBadge>
          <IntuiBadge variant="success">Success</IntuiBadge>
          <IntuiBadge variant="warning">Warning</IntuiBadge>
        </Subsection>
        <Subsection label="With icon & removable">
          <IntuiBadge icon={<CheckCircle2 />} variant="success">Verified</IntuiBadge>
          <IntuiBadge removable onRemove={() => {}}>Removable</IntuiBadge>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Input */}
      <Section title="Input">
        <Subsection label="Default">
          <IntuiInput placeholder="Enter text" className="w-[360px]" />
        </Subsection>
        <Subsection label="With Label + Description">
          <div className="w-[360px] space-y-2">
            <IntuiLabel htmlFor="username">Username</IntuiLabel>
            <IntuiInput id="username" placeholder="Matt Wierzbicki" />
            <p className="text-sm text-muted-foreground">Use the Field component if you want to use Input with Label and Description</p>
          </div>
        </Subsection>
        <Subsection label="Disabled">
          <div className="w-[360px] space-y-2">
            <IntuiLabel htmlFor="disabled-input">Username</IntuiLabel>
            <IntuiInput id="disabled-input" placeholder="Matt Wierzbicki" disabled />
            <p className="text-sm text-muted-foreground">Use the Field component if you want to use Input with Label and Description</p>
          </div>
        </Subsection>
        <Subsection label="Invalid">
          <div className="w-[360px] space-y-2">
            <IntuiLabel htmlFor="invalid-input" className="text-destructive">Username</IntuiLabel>
            <IntuiInput id="invalid-input" placeholder="Matt Wierzbicki" invalid />
            <p className="text-sm text-muted-foreground">Use the Field component if you want to use Input with Label and Description</p>
          </div>
        </Subsection>
        <Subsection label="File">
          <div className="w-[360px] space-y-2">
            <IntuiLabel htmlFor="file-input">File Upload</IntuiLabel>
            <IntuiInput id="file-input" type="file" />
            <p className="text-sm text-muted-foreground">Use the Field component if you want to use Input with Label and Description</p>
          </div>
        </Subsection>
        <Subsection label="Field Group (side-by-side)">
          <div className="flex gap-4 w-[480px]">
            <div className="flex-1 space-y-2">
              <IntuiLabel htmlFor="first-name">First Name</IntuiLabel>
              <IntuiInput id="first-name" placeholder="Jordan" />
            </div>
            <div className="flex-1 space-y-2">
              <IntuiLabel htmlFor="last-name">Last Name</IntuiLabel>
              <IntuiInput id="last-name" placeholder="Lee" />
            </div>
          </div>
        </Subsection>
        <Subsection label="Required Field">
          <div className="w-[360px] space-y-2">
            <IntuiLabel htmlFor="required-input" required>Required Field</IntuiLabel>
            <IntuiInput id="required-input" placeholder="This field is required" />
            <p className="text-sm text-muted-foreground">Add the star (*) with destructive color variable applied in the label above</p>
          </div>
        </Subsection>
        <Subsection label="With Badge">
          <div className="w-[360px] space-y-2">
            <div className="flex items-center justify-between">
              <IntuiLabel htmlFor="webhook-input">Webhook URL</IntuiLabel>
              <IntuiBadge variant="secondary">Beta</IntuiBadge>
            </div>
            <IntuiInput id="webhook-input" placeholder="https://www.shadcndesign.com" />
            <p className="text-sm text-muted-foreground">You can add Badge with position absolute in the parent frame</p>
          </div>
        </Subsection>
        <Subsection label="With Icon">
          <div className="w-[360px] space-y-2">
            <IntuiLabel htmlFor="url-input">Website URL</IntuiLabel>
            <div className="relative">
              <IntuiInput id="url-input" placeholder="https:// shadcndesign.com" className="pr-10" />
              <Globe className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
          </div>
        </Subsection>
        <Subsection label="Search with Button">
          <div className="w-[360px] space-y-2">
            <IntuiLabel htmlFor="search-input">Search</IntuiLabel>
            <div className="flex gap-2">
              <IntuiInput id="search-input" placeholder="Type to search..." className="flex-1" />
              <button className="px-4 h-8 bg-primary text-primary-foreground rounded-lg text-sm font-medium">Search</button>
            </div>
          </div>
        </Subsection>
        <Subsection label="Form Example">
          <div className="w-[360px] space-y-5">
            <div className="space-y-2">
              <IntuiLabel htmlFor="form-name">Name</IntuiLabel>
              <IntuiInput id="form-name" placeholder="ShadcnDesign" />
            </div>
            <div className="space-y-2">
              <IntuiLabel htmlFor="form-email">Email</IntuiLabel>
              <IntuiInput id="form-email" type="email" placeholder="hi@shadcndesign.com" />
              <p className="text-sm text-muted-foreground">We'll never share your email with anyone.</p>
            </div>
            <div className="flex gap-4">
              <div className="flex-1 space-y-2">
                <IntuiLabel htmlFor="form-phone">Phone</IntuiLabel>
                <IntuiInput id="form-phone" placeholder="+1 (123) 123-123" />
              </div>
              <div className="flex-1 space-y-2">
                <IntuiLabel htmlFor="form-country">Country</IntuiLabel>
                <IntuiInput id="form-country" placeholder="Poland" />
              </div>
            </div>
            <div className="space-y-2">
              <IntuiLabel htmlFor="form-address">Address</IntuiLabel>
              <IntuiInput id="form-address" placeholder="ul. Suchego Chleba 3" />
            </div>
            <div className="flex gap-2">
              <button className="px-4 h-8 bg-primary text-primary-foreground rounded-lg text-sm font-medium">Submit</button>
              <button className="px-4 h-8 text-sm font-medium">Cancel</button>
            </div>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Input Group */}
      <Section title="Input Group">
        <Subsection label="Search with icon + result count">
          <IntuiInputGroup startAddon={<Search />} endAddon={<IntuiInputGroupText>12 results</IntuiInputGroupText>} className="w-[373px]">
            <IntuiInputGroupInput placeholder="Search" />
          </IntuiInputGroup>
        </Subsection>

        <Subsection label="Password with eye toggle">
          <IntuiInputGroup endAddon={<button className="text-muted-foreground hover:text-foreground"><EyeOff className="size-4" /></button>} className="w-[373px]">
            <IntuiInputGroupInput type="password" placeholder="Enter password" />
          </IntuiInputGroup>
        </Subsection>

        <Subsection label="Textarea with counter + Post button">
          <IntuiInputGroup
            blockEnd={
              <>
                <IntuiInputGroupText>{commentValue.length}/{COMMENT_MAX} characters</IntuiInputGroupText>
                <div className="flex items-center gap-2">
                  <IntuiInputGroupText>{Math.round((commentValue.length / COMMENT_MAX) * 100)}% used</IntuiInputGroupText>
                  <button className="px-2 h-6 bg-primary text-primary-foreground rounded-md text-xs font-medium disabled:opacity-50" disabled={commentValue.length === 0}>Post</button>
                </div>
              </>
            }
            className="w-[373px]"
          >
            <IntuiInputGroupTextarea
              placeholder="Write a comment..."
              value={commentValue}
              onChange={(e) => {
                if (e.target.value.length <= COMMENT_MAX) setCommentValue(e.target.value)
              }}
            />
          </IntuiInputGroup>
        </Subsection>

        <Subsection label="Verified input (check icon)">
          <IntuiInputGroup endAddon={<CheckCircle2 className="size-4 text-foreground" />} className="w-[373px]">
            <IntuiInputGroupInput placeholder="@shadcn" />
          </IntuiInputGroup>
        </Subsection>

        <Subsection label="Icon addons">
          <div className="space-y-3 w-[373px]">
            <IntuiInputGroup startAddon={<Search />}>
              <IntuiInputGroupInput placeholder="Search" />
            </IntuiInputGroup>
            <IntuiInputGroup startAddon={<Mail />}>
              <IntuiInputGroupInput placeholder="Enter your email" />
            </IntuiInputGroup>
            <IntuiInputGroup startAddon={<CreditCard />} endAddon={<CheckCircle2 className="size-4 text-foreground" />}>
              <IntuiInputGroupInput placeholder="Card number" />
            </IntuiInputGroup>
            <IntuiInputGroup startAddon={<CreditCard />} endAddon={<><Star className="size-4" /><Globe className="size-4" /></>}>
              <IntuiInputGroupInput placeholder="Card number" />
            </IntuiInputGroup>
          </div>
        </Subsection>

        <Subsection label="Text addons">
          <div className="space-y-3 w-[373px]">
            <IntuiInputGroup startAddon={<IntuiInputGroupText>$</IntuiInputGroupText>} endAddon={<IntuiInputGroupText>USD</IntuiInputGroupText>}>
              <IntuiInputGroupInput placeholder="0.00" />
            </IntuiInputGroup>
            <IntuiInputGroup startAddon={<IntuiInputGroupText>https://</IntuiInputGroupText>} endAddon={<IntuiInputGroupText>.com</IntuiInputGroupText>}>
              <IntuiInputGroupInput placeholder="Enter your email" />
            </IntuiInputGroup>
            <IntuiInputGroup endAddon={<IntuiInputGroupText>@company.com</IntuiInputGroupText>}>
              <IntuiInputGroupInput placeholder="Enter your username" />
            </IntuiInputGroup>
          </div>
        </Subsection>

        <Subsection label="Textarea with character count">
          <IntuiInputGroup blockEnd={<IntuiInputGroupText>{MESSAGE_MAX - messageValue.length} characters left</IntuiInputGroupText>} className="w-[373px]">
            <IntuiInputGroupTextarea
              placeholder="Enter your message"
              value={messageValue}
              onChange={(e) => {
                if (e.target.value.length <= MESSAGE_MAX) setMessageValue(e.target.value)
              }}
            />
          </IntuiInputGroup>
        </Subsection>

        <Subsection label="Social link with copy">
          <IntuiInputGroup
            endAddon={
              <button
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(socialLink)
                    setSocialCopied(true)
                    setTimeout(() => setSocialCopied(false), 2000)
                  } catch { /* clipboard denied */ }
                }}
                className="text-muted-foreground hover:text-foreground"
                title={socialCopied ? 'Copied!' : 'Copy'}
              >
                {socialCopied ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
              </button>
            }
            className="w-[373px]"
          >
            <IntuiInputGroupInput defaultValue={socialLink} />
          </IntuiInputGroup>
        </Subsection>

        <Subsection label="URL with globe + bookmark">
          <IntuiInputGroup startAddon={<Globe />} endAddon={<button onClick={() => setBookmarked(!bookmarked)} className="text-muted-foreground hover:text-foreground"><Bookmark className={cn('size-4', bookmarked && 'fill-current text-foreground')} /></button>} className="w-[373px]">
            <IntuiInputGroupInput placeholder="https://" />
          </IntuiInputGroup>
        </Subsection>

        <Subsection label="Search with inline button">
          <IntuiInputGroup endAddon={<button className="px-2 h-6 bg-primary text-primary-foreground rounded-md text-xs font-medium">Search</button>} className="w-[373px]">
            <IntuiInputGroupInput placeholder="Type to search..." />
          </IntuiInputGroup>
        </Subsection>

        <Subsection label="Search with Kbd shortcut">
          <IntuiInputGroup startAddon={<Search />} endAddon={<IntuiKbd>⌘K</IntuiKbd>} className="w-[373px]">
            <IntuiInputGroupInput placeholder="Search" />
          </IntuiInputGroup>
        </Subsection>

        <Subsection label="Loading states">
          <div className="space-y-3 w-[373px]">
            <IntuiInputGroup startAddon={<Search />} endAddon={<Loader2 className="size-4 animate-spin" />}>
              <IntuiInputGroupInput placeholder="Searching..." />
            </IntuiInputGroup>
            <IntuiInputGroup startAddon={<Loader2 className="size-4 animate-spin" />}>
              <IntuiInputGroupInput placeholder="Processing..." />
            </IntuiInputGroup>
            <IntuiInputGroup endAddon={<><IntuiInputGroupText>Saving...</IntuiInputGroupText><Loader2 className="size-4 animate-spin" /></>}>
              <IntuiInputGroupInput placeholder="Saving changes..." />
            </IntuiInputGroup>
          </div>
        </Subsection>

        <Subsection label="Code editor textarea">
          <IntuiInputGroup
            blockStart={
              <>
                <div className="flex items-center gap-1.5">
                  <FileText className="size-4 text-muted-foreground" />
                  <IntuiInputGroupText>script.js</IntuiInputGroupText>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(codeValue)
                        setCodeCopied(true)
                        setTimeout(() => setCodeCopied(false), 2000)
                      } catch { /* clipboard denied */ }
                    }}
                    className="text-muted-foreground hover:text-foreground"
                    title={codeCopied ? 'Copied!' : 'Copy'}
                  >
                    {codeCopied ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        const text = await navigator.clipboard.readText()
                        setCodeValue((prev) => prev + text)
                      } catch { /* clipboard denied */ }
                    }}
                    className="text-muted-foreground hover:text-foreground"
                    title="Paste"
                  >
                    <Clipboard className="size-4" />
                  </button>
                </div>
              </>
            }
            blockEnd={
              <>
                <IntuiInputGroupText>Line 1, Column 1</IntuiInputGroupText>
                <button className="px-2 h-6 bg-primary text-primary-foreground rounded-md text-xs font-medium">Post</button>
              </>
            }
            className="w-[373px]"
          >
            <IntuiInputGroupTextarea
              value={codeValue}
              onChange={(e) => setCodeValue(e.target.value)}
              className="font-mono"
            />
          </IntuiInputGroup>
        </Subsection>

        <Subsection label="Autosize textarea with submit">
          <IntuiInputGroup blockEnd={<button className="ml-auto px-3 h-6 bg-primary text-primary-foreground rounded-md text-xs font-medium">Submit</button>} className="w-[373px]">
            <IntuiInputGroupTextarea
              placeholder="Autosize textarea..."
              value={autosizeValue}
              onChange={(e) => {
                setAutosizeValue(e.target.value)
                e.target.style.height = 'auto'
                e.target.style.height = `${e.target.scrollHeight}px`
              }}
              className="overflow-hidden"
            />
          </IntuiInputGroup>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Field */}
      <Section title="Field">
        <Subsection label="FieldSet (Payment Method form)">
          <div className="w-[448px] space-y-5">
            <IntuiFieldLegend
              title="Payment Method"
              description="Lorem ipsum dolor sit amet, consectetur adipiscing elit interdum hendrerit ex vitae sodales."
            />
            <IntuiField label="Name on Card" htmlFor="card-name">
              <IntuiInput id="card-name" placeholder="Evil Rabbit" />
            </IntuiField>
            <IntuiField label="Card Number" htmlFor="card-number" description="Enter your 16-digit card number">
              <IntuiInput id="card-number" placeholder="1234 5678 9012 3456" />
            </IntuiField>
            <IntuiFieldGroup>
              <IntuiField label="Month" htmlFor="card-month" className="flex-1">
                <Select>
                  <SelectTrigger className="h-8 rounded-lg"><SelectValue placeholder="MM" /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </IntuiField>
              <IntuiField label="Year" htmlFor="card-year" className="flex-1">
                <Select>
                  <SelectTrigger className="h-8 rounded-lg"><SelectValue placeholder="YYYY" /></SelectTrigger>
                  <SelectContent>
                    {['2025', '2026', '2027', '2028', '2029', '2030'].map((y) => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </IntuiField>
              <IntuiField label="CVV" htmlFor="card-cvv" className="flex-1">
                <IntuiInput id="card-cvv" placeholder="123" maxLength={4} />
              </IntuiField>
            </IntuiFieldGroup>
            <IntuiFieldSeparator />
            <IntuiFieldLegend title="Billing Address" description="The billing address associated with your payment method" />
            <IntuiCheckbox label="Same as shipping address" checked={sameAsShipping} onCheckedChange={setSameAsShipping} />
            <IntuiField label="Comments" htmlFor="comments">
              <IntuiInput id="comments" placeholder="Add any additional comments" />
            </IntuiField>
            <IntuiFieldButtons>
              <button className="px-4 h-8 bg-primary text-primary-foreground rounded-lg text-sm font-medium">Submit</button>
              <button className="px-4 h-8 text-sm font-medium">Cancel</button>
            </IntuiFieldButtons>
          </div>
        </Subsection>

        <Subsection label="Username (description under input)">
          <IntuiField label="Username" htmlFor="field-username" description="Choose a unique username for your account." className="w-[448px]">
            <IntuiInput id="field-username" placeholder="Matt Wierzbicki" />
          </IntuiField>
        </Subsection>

        <Subsection label="Password (description under label)">
          <IntuiField label="Password" htmlFor="field-password" description="Must be at least 8 characters long." descriptionPlacement="under-label" className="w-[448px]">
            <IntuiInput id="field-password" type="password" placeholder="••••••••••" />
          </IntuiField>
        </Subsection>

        <Subsection label="Feedback (textarea)">
          <IntuiField label="Feedback" htmlFor="field-feedback" description="Share your thoughts with us." className="w-[448px]">
            <textarea id="field-feedback" placeholder="Your feedback helps us improve..." className="flex w-full rounded-lg border border-input bg-background px-2.5 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[80px] resize-none" />
          </IntuiField>
        </Subsection>

        <Subsection label="Department (select)">
          <IntuiField label="Department" htmlFor="field-dept" description="Select your department or area of work." className="w-[448px]">
            <Select>
              <SelectTrigger className="h-8 rounded-lg"><SelectValue placeholder="Choose department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="engineering">Engineering</SelectItem>
                <SelectItem value="design">Design</SelectItem>
                <SelectItem value="product">Product</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
              </SelectContent>
            </Select>
          </IntuiField>
        </Subsection>

        <Subsection label="Price Range (slider)">
          <IntuiField label="Price Range" description={`Set your budget range ($${sliderValue[0]} – $${sliderValue[1]}).`} className="w-[448px]">
            <IntuiSlider min={0} max={1000} step={10} value={sliderValue} onValueChange={setSliderValue} />
          </IntuiField>
        </Subsection>

        <Subsection label="Checkbox group">
          <div className="w-[448px] space-y-3">
            <IntuiFieldLegend title="Show these items on the desktop" description="Select the items you want to show on the desktop." />
            <div className="space-y-2">
              <IntuiCheckbox label="Hard disks" checked={hardDisks} onCheckedChange={setHardDisks} />
              <IntuiCheckbox label="External disks" checked={externalDisks} onCheckedChange={setExternalDisks} />
              <IntuiCheckbox label="CDs, DVDs, and iPods" checked={cds} onCheckedChange={setCds} />
              <IntuiCheckbox label="Connected servers" checked={connectedServers} onCheckedChange={setConnectedServers} />
            </div>
          </div>
        </Subsection>

        <Subsection label="Radio group (Subscription Plan)">
          <div className="w-[448px] space-y-3">
            <IntuiFieldLegend title="Subscription Plan" description="Yearly and lifetime plans offer significant savings." />
            <RadioGroup value={subscriptionPlan} onValueChange={setSubscriptionPlan}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="monthly" id="plan-monthly" />
                <label htmlFor="plan-monthly" className="text-sm">Monthly ($9.99/month)</label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yearly" id="plan-yearly" />
                <label htmlFor="plan-yearly" className="text-sm">Yearly ($96.99/year)</label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="lifetime" id="plan-lifetime" />
                <label htmlFor="plan-lifetime" className="text-sm">Lifetime ($299.99)</label>
              </div>
            </RadioGroup>
          </div>
        </Subsection>

        <Subsection label="Switch (Multi-factor auth)">
          <div className="w-[448px] flex justify-center">
            <IntuiSwitch label="Multi-factor authentication" checked={mfaEnabled} onCheckedChange={setMfaEnabled} />
          </div>
        </Subsection>

        <Subsection label="Compute Environment (radio cards)">
          <div className="w-[448px] space-y-3">
            <IntuiFieldLegend title="Compute Environment" description="Select the compute environment for your cluster." />
            <RadioGroup value={computeEnv} onValueChange={setComputeEnv} className="space-y-2">
              <label htmlFor="env-k8s" className={cn('flex items-start gap-3 rounded-lg border p-4 cursor-pointer', computeEnv === 'kubernetes' ? 'border-foreground' : 'border-input')}>
                <RadioGroupItem value="kubernetes" id="env-k8s" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Kubernetes</p>
                  <p className="text-sm text-muted-foreground">Run GPU workloads on a K8s cluster.</p>
                </div>
              </label>
              <label htmlFor="env-vm" className={cn('flex items-start gap-3 rounded-lg border p-4 cursor-pointer', computeEnv === 'vm' ? 'border-foreground' : 'border-input')}>
                <RadioGroupItem value="vm" id="env-vm" className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Virtual Machine</p>
                  <p className="text-sm text-muted-foreground">Access a cluster to run GPU workloads.</p>
                </div>
              </label>
            </RadioGroup>
          </div>
        </Subsection>

        <Subsection label="Invalid field">
          <IntuiField label="Username" htmlFor="field-invalid" description="Choose a unique username for your account." invalid className="w-[448px]">
            <IntuiInput id="field-invalid" placeholder="Matt Wierzbicki" invalid />
          </IntuiField>
        </Subsection>

        <Subsection label="Responsive (side-by-side)">
          <IntuiField label="Name" htmlFor="field-responsive" description="Your full name" orientation="responsive" className="w-[448px]">
            <IntuiInput id="field-responsive" placeholder="Matt Wierzbicki" />
          </IntuiField>
        </Subsection>

        <Subsection label="Responsive invalid">
          <IntuiField label="Name" htmlFor="field-responsive-inv" description="This field is required." orientation="responsive" invalid className="w-[448px]">
            <IntuiInput id="field-responsive-inv" placeholder="Matt Wierzbicki" invalid />
          </IntuiField>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Input OTP */}
      <Section title="Input OTP">
        <Subsection label="Simple (3+3 with separator)">
          <IntuiInputOTP maxLength={6} pattern={REGEXP_ONLY_DIGITS}>
            <IntuiInputOTPGroup>
              <IntuiInputOTPSlot index={0} />
              <IntuiInputOTPSlot index={1} />
              <IntuiInputOTPSlot index={2} />
            </IntuiInputOTPGroup>
            <IntuiInputOTPSeparator />
            <IntuiInputOTPGroup>
              <IntuiInputOTPSlot index={3} />
              <IntuiInputOTPSlot index={4} />
              <IntuiInputOTPSlot index={5} />
            </IntuiInputOTPGroup>
          </IntuiInputOTP>
        </Subsection>

        <Subsection label="Digits Only (6 connected)">
          <IntuiInputOTP maxLength={6} pattern={REGEXP_ONLY_DIGITS}>
            <IntuiInputOTPGroup>
              <IntuiInputOTPSlot index={0} />
              <IntuiInputOTPSlot index={1} />
              <IntuiInputOTPSlot index={2} />
              <IntuiInputOTPSlot index={3} />
              <IntuiInputOTPSlot index={4} />
              <IntuiInputOTPSlot index={5} />
            </IntuiInputOTPGroup>
          </IntuiInputOTP>
        </Subsection>

        <Subsection label="With Separator (2+2+2)">
          <IntuiInputOTP maxLength={6} pattern={REGEXP_ONLY_DIGITS}>
            <IntuiInputOTPGroup>
              <IntuiInputOTPSlot index={0} />
              <IntuiInputOTPSlot index={1} />
            </IntuiInputOTPGroup>
            <IntuiInputOTPSeparator />
            <IntuiInputOTPGroup>
              <IntuiInputOTPSlot index={2} />
              <IntuiInputOTPSlot index={3} />
            </IntuiInputOTPGroup>
            <IntuiInputOTPSeparator />
            <IntuiInputOTPGroup>
              <IntuiInputOTPSlot index={4} />
              <IntuiInputOTPSlot index={5} />
            </IntuiInputOTPGroup>
          </IntuiInputOTP>
        </Subsection>

        <Subsection label="With Spacing (6 separate)">
          <IntuiInputOTP maxLength={6} pattern={REGEXP_ONLY_DIGITS} containerClassName="gap-2">
            <IntuiInputOTPSpacedSlot index={0} />
            <IntuiInputOTPSpacedSlot index={1} />
            <IntuiInputOTPSpacedSlot index={2} />
            <IntuiInputOTPSpacedSlot index={3} />
            <IntuiInputOTPSpacedSlot index={4} />
            <IntuiInputOTPSpacedSlot index={5} />
          </IntuiInputOTP>
        </Subsection>

        <Subsection label="Invalid (Simple with separator)">
          <IntuiInputOTP maxLength={6} pattern={REGEXP_ONLY_DIGITS}>
            <IntuiInputOTPGroup invalid>
              <IntuiInputOTPSlot index={0} invalid />
              <IntuiInputOTPSlot index={1} invalid />
            </IntuiInputOTPGroup>
            <IntuiInputOTPSeparator />
            <IntuiInputOTPGroup invalid>
              <IntuiInputOTPSlot index={2} invalid />
              <IntuiInputOTPSlot index={3} invalid />
            </IntuiInputOTPGroup>
            <IntuiInputOTPSeparator />
            <IntuiInputOTPGroup invalid>
              <IntuiInputOTPSlot index={4} invalid />
              <IntuiInputOTPSlot index={5} invalid />
            </IntuiInputOTPGroup>
          </IntuiInputOTP>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Inputs & Controls */}
      <Section title="Inputs & Controls">
        <Subsection label="Switch">
          <IntuiSwitch checked={switchChecked} onCheckedChange={setSwitchChecked} label="Airplane Mode" />
        </Subsection>
        <Subsection label="Switch with label and description">
          <IntuiSwitch
            checked={switchChecked}
            onCheckedChange={setSwitchChecked}
            label="Airplane mode"
            description="Disable all wireless connections."
          />
        </Subsection>
        <Subsection label="Switch states">
          <div className="space-y-3">
            <IntuiSwitch label="Off" />
            <IntuiSwitch label="On" checked />
            <IntuiSwitch label="Disabled" disabled />
          </div>
        </Subsection>
        <Subsection label="Checkbox">
          <div className="space-y-3">
            <IntuiCheckbox checked={checkboxChecked} onCheckedChange={setCheckboxChecked} label="Accept terms" description="You agree to our Terms of Service." />
            <IntuiCheckbox checked="indeterminate" label="Indeterminate" description="This is a checkbox description." />
            <IntuiCheckbox checked disabled label="Disabled checked" description="This is a checkbox description." />
            <IntuiCheckbox disabled label="Disabled unchecked" description="This is a checkbox description." />
            <IntuiCheckbox checked invalid label="Invalid checked" description="This is a checkbox description." />
            <IntuiCheckbox invalid label="Invalid unchecked" description="This is a checkbox description." />
          </div>
        </Subsection>
        <Subsection label="Toggle variants">
          <IntuiToggle pressed={togglePressed} onPressedChange={setTogglePressed} aria-label="Toggle bold"><Bold className="h-4 w-4" /></IntuiToggle>
          <IntuiToggle variant="outline" aria-label="Toggle italic"><Italic className="h-4 w-4" /></IntuiToggle>
        </Subsection>
        <Subsection label="Toggle sizes">
          <IntuiToggle size="sm" aria-label="Small"><Bold className="h-3 w-3" /></IntuiToggle>
          <IntuiToggle size="default" aria-label="Default"><Bold className="h-4 w-4" /></IntuiToggle>
          <IntuiToggle size="lg" aria-label="Large"><Bold className="h-5 w-5" /></IntuiToggle>
        </Subsection>
        <Subsection label="Toggle disabled">
          <IntuiToggle disabled aria-label="Disabled"><Bold className="h-4 w-4" /></IntuiToggle>
        </Subsection>
        <Subsection label="Label">
          <IntuiLabel>Email Address</IntuiLabel>
          <IntuiLabel required>Required Field</IntuiLabel>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Feedback */}
      <Section title="Feedback">
        <Subsection label="Alert">
          <div className="w-full space-y-3">
            <IntuiAlert title="Default Alert" description="This is a default alert message." />
            <IntuiAlert variant="destructive" title="Destructive Alert" description="Something went wrong." />
            <IntuiAlert variant="success" title="Success" icon={<CheckCircle2 />} description="Operation completed." />
            <IntuiAlert variant="warning" title="Warning" icon={<AlertCircle />} description="Please review your changes." />
          </div>
        </Subsection>
        <Subsection label="Progress">
          <div className="w-full max-w-md space-y-3">
            <IntuiProgress value={25} />
            <IntuiProgress value={50} />
            <IntuiProgress value={75} />
            <IntuiProgress value={100} />
          </div>
        </Subsection>
        <Subsection label="Spinner">
          <IntuiSpinner size="sm" />
          <IntuiSpinner size="md" />
          <IntuiSpinner size="lg" />
        </Subsection>
        <Subsection label="Skeleton shapes">
          <IntuiSkeleton width="200px" height="20px" />
          <IntuiSkeleton shape="circle" width="48px" height="48px" />
        </Subsection>
        <Subsection label="Skeleton card placeholder">
          <div className="flex items-center gap-4">
            <IntuiSkeleton shape="circle" width="40px" height="40px" />
            <div className="space-y-2">
              <IntuiSkeleton width="200px" height="16px" />
              <IntuiSkeleton width="150px" height="12px" />
            </div>
          </div>
        </Subsection>
        <Subsection label="Skeleton content block">
          <div className="space-y-2 w-[300px]">
            <IntuiSkeleton width="100%" height="16px" />
            <IntuiSkeleton width="80%" height="16px" />
            <IntuiSkeleton width="60%" height="16px" />
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Overlays */}
      <Section title="Overlays">
        <Subsection label="Share link (from Figma)">
          <IntuiDialog
            title="Share link"
            description="Anyone who has this link will be able to view this."
            trigger={<IntuiButton variant="outline">Share Link</IntuiButton>}
            footer={<IntuiDialogClose asChild><IntuiButton>Close</IntuiButton></IntuiDialogClose>}
          >
            <div className="pb-4 px-4">
              <IntuiInput defaultValue="https://www.shadcndesign.com" readOnly />
            </div>
          </IntuiDialog>
        </Subsection>
        <Subsection label="Edit profile (form)">
          <IntuiDialog
            title="Dialog title"
            description="This is a dialog description."
            trigger={<IntuiButton variant="outline">Edit Profile</IntuiButton>}
            footer={<><IntuiDialogClose asChild><IntuiButton variant="outline">Cancel</IntuiButton></IntuiDialogClose><IntuiButton>Save changes</IntuiButton></>}
          >
            <div className="grid gap-4 py-4">
              <div className="space-y-2"><IntuiLabel htmlFor="p1-dialog-name">Name</IntuiLabel><IntuiInput id="p1-dialog-name" placeholder="Placeholder" /></div>
              <div className="space-y-2"><IntuiLabel htmlFor="p1-dialog-username">Username</IntuiLabel><IntuiInput id="p1-dialog-username" placeholder="Placeholder" /></div>
            </div>
          </IntuiDialog>
        </Subsection>
        <Subsection label="Tooltip positions">
          <IntuiTooltip content="Top tooltip" side="top"><IntuiButton variant="outline" size="sm">Top</IntuiButton></IntuiTooltip>
          <IntuiTooltip content="Bottom tooltip" side="bottom"><IntuiButton variant="outline" size="sm">Bottom</IntuiButton></IntuiTooltip>
          <IntuiTooltip content="Left tooltip" side="left"><IntuiButton variant="outline" size="sm">Left</IntuiButton></IntuiTooltip>
          <IntuiTooltip content="Right tooltip" side="right"><IntuiButton variant="outline" size="sm">Right</IntuiButton></IntuiTooltip>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Dropdown Menu */}
      <Section title="Dropdown Menu">
        <Subsection label="Basic (with icons + shortcuts)">
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <button className="px-4 py-2 border rounded-md text-sm">Open</button>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent className="w-56">
              <IntuiDropdownMenuLabel>My Account</IntuiDropdownMenuLabel>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuGroup>
                <IntuiDropdownMenuItem shortcut="⇧⌘P">Profile</IntuiDropdownMenuItem>
                <IntuiDropdownMenuItem shortcut="⌘B">Billing</IntuiDropdownMenuItem>
                <IntuiDropdownMenuItem shortcut="⌘S">Settings</IntuiDropdownMenuItem>
              </IntuiDropdownMenuGroup>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuItem>Team</IntuiDropdownMenuItem>
              <IntuiDropdownMenuSub>
                <IntuiDropdownMenuSubTrigger>Invite users</IntuiDropdownMenuSubTrigger>
                <IntuiDropdownMenuSubContent>
                  <IntuiDropdownMenuItem icon={<Mail className="h-4 w-4" />}>Email</IntuiDropdownMenuItem>
                  <IntuiDropdownMenuItem icon={<MessageSquare className="h-4 w-4" />}>Message</IntuiDropdownMenuItem>
                </IntuiDropdownMenuSubContent>
              </IntuiDropdownMenuSub>
              <IntuiDropdownMenuItem shortcut="⌘+T">New Team</IntuiDropdownMenuItem>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuItem>GitHub</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem>Support</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem disabled>API</IntuiDropdownMenuItem>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuItem shortcut="⇧⌘Q">Log out</IntuiDropdownMenuItem>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </Subsection>

        <Subsection label="Shortcuts only">
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <button className="px-4 py-2 border rounded-md text-sm">Open</button>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent className="w-48">
              <IntuiDropdownMenuLabel>My Account</IntuiDropdownMenuLabel>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuItem shortcut="⇧⌘P">Profile</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem shortcut="⌘B">Billing</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem shortcut="⌘S">Settings</IntuiDropdownMenuItem>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuItem shortcut="⇧⌘Q">Log out</IntuiDropdownMenuItem>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </Subsection>

        <Subsection label="Icons only">
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <button className="px-4 py-2 border rounded-md text-sm">Open</button>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent className="w-48">
              <IntuiDropdownMenuItem icon={<User className="h-4 w-4" />}>Profile</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem icon={<CreditCard className="h-4 w-4" />}>Billing</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem icon={<Settings className="h-4 w-4" />}>Settings</IntuiDropdownMenuItem>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuItem variant="destructive" icon={<LogOut className="h-4 w-4" />}>Log out</IntuiDropdownMenuItem>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </Subsection>

        <Subsection label="Submenu">
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <button className="px-4 py-2 border rounded-md text-sm">Open</button>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent className="w-48">
              <IntuiDropdownMenuItem icon={<Copy className="h-4 w-4" />} shortcut="⌘C">Copy</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem icon={<Scissors className="h-4 w-4" />} shortcut="⌘X">Cut</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem icon={<Clipboard className="h-4 w-4" />} shortcut="⌘V">Paste</IntuiDropdownMenuItem>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuSub>
                <IntuiDropdownMenuSubTrigger>
                  <Mail className="mr-2 h-4 w-4" />
                  Invite users
                </IntuiDropdownMenuSubTrigger>
                <IntuiDropdownMenuSubContent>
                  <IntuiDropdownMenuItem icon={<Mail className="h-4 w-4" />}>Email</IntuiDropdownMenuItem>
                  <IntuiDropdownMenuItem icon={<MessageSquare className="h-4 w-4" />}>Message</IntuiDropdownMenuItem>
                </IntuiDropdownMenuSubContent>
              </IntuiDropdownMenuSub>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </Subsection>

        <Subsection label="Checkboxes">
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <button className="px-4 py-2 border rounded-md text-sm">Open</button>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent className="w-56">
              <IntuiDropdownMenuLabel>Appearance</IntuiDropdownMenuLabel>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuCheckboxItem checked={showBookmarks} onCheckedChange={setShowBookmarks}>
                Show Bookmarks
              </IntuiDropdownMenuCheckboxItem>
              <IntuiDropdownMenuCheckboxItem checked={showUrls} onCheckedChange={setShowUrls}>
                Show Full URLs
              </IntuiDropdownMenuCheckboxItem>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </Subsection>

        <Subsection label="Checkboxes with Icons">
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <button className="px-4 py-2 border rounded-md text-sm">Notifications</button>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent className="w-56">
              <IntuiDropdownMenuLabel>Notification Preferences</IntuiDropdownMenuLabel>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuCheckboxItem checked={emailNotif} onCheckedChange={setEmailNotif}>
                <Mail className="mr-2 h-4 w-4" /> Email notifications
              </IntuiDropdownMenuCheckboxItem>
              <IntuiDropdownMenuCheckboxItem checked={smsNotif} onCheckedChange={setSmsNotif}>
                <MessageSquare className="mr-2 h-4 w-4" /> SMS notifications
              </IntuiDropdownMenuCheckboxItem>
              <IntuiDropdownMenuCheckboxItem checked={pushNotif} onCheckedChange={setPushNotif}>
                <Bell className="mr-2 h-4 w-4" /> Push notifications
              </IntuiDropdownMenuCheckboxItem>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </Subsection>

        <Subsection label="Radio Group">
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <button className="px-4 py-2 border rounded-md text-sm">Open</button>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent className="w-56">
              <IntuiDropdownMenuLabel>Team Members</IntuiDropdownMenuLabel>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuRadioGroup value={radioValue} onValueChange={setRadioValue}>
                <IntuiDropdownMenuRadioItem value="pedro">Pedro</IntuiDropdownMenuRadioItem>
                <IntuiDropdownMenuRadioItem value="colm">Colm</IntuiDropdownMenuRadioItem>
                <IntuiDropdownMenuRadioItem value="rafiu">Rafiu</IntuiDropdownMenuRadioItem>
              </IntuiDropdownMenuRadioGroup>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </Subsection>

        <Subsection label="Avatar Trigger">
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <button className="rounded-full overflow-hidden">
                <IntuiAvatar src="https://github.com/shadcn.png" fallback="CN" />
              </button>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent className="w-48">
              <IntuiDropdownMenuItem icon={<User className="h-4 w-4" />}>Account</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem icon={<CreditCard className="h-4 w-4" />}>Billing</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem icon={<Bell className="h-4 w-4" />}>Notifications</IntuiDropdownMenuItem>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuItem icon={<LogOut className="h-4 w-4" />}>Sign out</IntuiDropdownMenuItem>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </Subsection>

        <Subsection label="Destructive Items">
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <button className="px-4 py-2 border rounded-md text-sm">Open</button>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent className="w-56">
              <IntuiDropdownMenuItem icon={<Settings className="h-4 w-4" />}>Settings</IntuiDropdownMenuItem>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuItem variant="destructive" icon={<Trash2 className="h-4 w-4" />} shortcut="⌘⌫">
                Delete
              </IntuiDropdownMenuItem>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </Subsection>

        <Subsection label="Complex (multi-section)">
          <IntuiDropdownMenu>
            <IntuiDropdownMenuTrigger asChild>
              <button className="px-4 py-2 border rounded-md text-sm">Complex Menu</button>
            </IntuiDropdownMenuTrigger>
            <IntuiDropdownMenuContent className="w-48">
              <IntuiDropdownMenuLabel>File</IntuiDropdownMenuLabel>
              <IntuiDropdownMenuGroup>
                <IntuiDropdownMenuItem icon={<FileText className="h-4 w-4" />} shortcut="⌘N">New File</IntuiDropdownMenuItem>
                <IntuiDropdownMenuItem icon={<FolderPlus className="h-4 w-4" />} shortcut="⇧⌘N">New Folder</IntuiDropdownMenuItem>
                <IntuiDropdownMenuSub>
                  <IntuiDropdownMenuSubTrigger>
                    <FolderOpen className="mr-2 h-4 w-4" />
                    Open Recent
                  </IntuiDropdownMenuSubTrigger>
                  <IntuiDropdownMenuSubContent>
                    <IntuiDropdownMenuItem>Document 1</IntuiDropdownMenuItem>
                    <IntuiDropdownMenuItem>Document 2</IntuiDropdownMenuItem>
                    <IntuiDropdownMenuItem>Document 3</IntuiDropdownMenuItem>
                  </IntuiDropdownMenuSubContent>
                </IntuiDropdownMenuSub>
                <IntuiDropdownMenuItem icon={<Save className="h-4 w-4" />} shortcut="⌘S">Save</IntuiDropdownMenuItem>
                <IntuiDropdownMenuItem icon={<Download className="h-4 w-4" />} shortcut="⇧⌘E">Export</IntuiDropdownMenuItem>
              </IntuiDropdownMenuGroup>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuLabel>View</IntuiDropdownMenuLabel>
              <IntuiDropdownMenuGroup>
                <IntuiDropdownMenuCheckboxItem checked={showSidebar} onCheckedChange={setShowSidebar}>
                  <Eye className="mr-2 h-4 w-4" /> Show Sidebar
                </IntuiDropdownMenuCheckboxItem>
                <IntuiDropdownMenuCheckboxItem checked={showStatusBar} onCheckedChange={setShowStatusBar}>
                  Show Status Bar
                </IntuiDropdownMenuCheckboxItem>
                <IntuiDropdownMenuSub>
                  <IntuiDropdownMenuSubTrigger>
                    <Palette className="mr-2 h-4 w-4" />
                    Theme
                  </IntuiDropdownMenuSubTrigger>
                  <IntuiDropdownMenuSubContent>
                    <IntuiDropdownMenuItem>Light</IntuiDropdownMenuItem>
                    <IntuiDropdownMenuItem>Dark</IntuiDropdownMenuItem>
                    <IntuiDropdownMenuItem>System</IntuiDropdownMenuItem>
                  </IntuiDropdownMenuSubContent>
                </IntuiDropdownMenuSub>
              </IntuiDropdownMenuGroup>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuLabel>Account</IntuiDropdownMenuLabel>
              <IntuiDropdownMenuGroup>
                <IntuiDropdownMenuItem icon={<User className="h-4 w-4" />} shortcut="⇧⌘P">Profile</IntuiDropdownMenuItem>
                <IntuiDropdownMenuItem icon={<CreditCard className="h-4 w-4" />}>Billing</IntuiDropdownMenuItem>
                <IntuiDropdownMenuSub>
                  <IntuiDropdownMenuSubTrigger>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </IntuiDropdownMenuSubTrigger>
                  <IntuiDropdownMenuSubContent>
                    <IntuiDropdownMenuItem>General</IntuiDropdownMenuItem>
                    <IntuiDropdownMenuItem>Privacy</IntuiDropdownMenuItem>
                    <IntuiDropdownMenuItem>Notifications</IntuiDropdownMenuItem>
                  </IntuiDropdownMenuSubContent>
                </IntuiDropdownMenuSub>
              </IntuiDropdownMenuGroup>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuItem icon={<HelpCircle className="h-4 w-4" />}>Help & Support</IntuiDropdownMenuItem>
              <IntuiDropdownMenuItem icon={<BookOpen className="h-4 w-4" />}>Documentation</IntuiDropdownMenuItem>
              <IntuiDropdownMenuSeparator />
              <IntuiDropdownMenuItem variant="destructive" icon={<LogOut className="h-4 w-4" />} shortcut="⇧⌘Q">
                Sign out
              </IntuiDropdownMenuItem>
            </IntuiDropdownMenuContent>
          </IntuiDropdownMenu>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Data Display */}
      <Section title="Data Display">
        <Subsection label="Avatar">
          <IntuiAvatar fallback="RK" />
          <IntuiAvatar src="https://github.com/shadcn.png" fallback="CN" />
          <IntuiAvatar fallback="AB" size="lg" />
        </Subsection>
        <Subsection label="Kbd">
          <IntuiKbd>⌘</IntuiKbd>
          <IntuiKbd>K</IntuiKbd>
          <span className="text-sm text-muted-foreground">Press</span>
          <IntuiKbd>⌘</IntuiKbd>
          <IntuiKbd>⇧</IntuiKbd>
          <IntuiKbd>P</IntuiKbd>
          <span className="text-sm text-muted-foreground">for command palette</span>
        </Subsection>
        <Subsection label="Separator">
          <div className="w-full space-y-4">
            <IntuiSeparator />
            <IntuiSeparator label="OR" />
            <div className="flex h-8 items-center gap-4">
              <span>Left</span>
              <IntuiSeparator orientation="vertical" />
              <span>Right</span>
            </div>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Resizable */}
      <Section title="Resizable">
        <Subsection label="Horizontal (two panels)">
          <IntuiResizablePanelGroup orientation="horizontal" className="h-[300px] max-w-[480px]">
            <IntuiResizablePanel defaultSize={50}>
              <div className="flex h-full items-center justify-center">
                <span className="text-xl font-medium">One</span>
              </div>
            </IntuiResizablePanel>
            <IntuiResizableHandle />
            <IntuiResizablePanel defaultSize={50}>
              <div className="flex h-full items-center justify-center">
                <span className="text-xl font-medium">Two</span>
              </div>
            </IntuiResizablePanel>
          </IntuiResizablePanelGroup>
        </Subsection>

        <Subsection label="Vertical (stacked)">
          <IntuiResizablePanelGroup orientation="vertical" className="h-[300px] max-w-[480px]">
            <IntuiResizablePanel defaultSize={50}>
              <div className="flex h-full items-center justify-center">
                <span className="text-xl font-medium">Top</span>
              </div>
            </IntuiResizablePanel>
            <IntuiResizableHandle />
            <IntuiResizablePanel defaultSize={50}>
              <div className="flex h-full items-center justify-center">
                <span className="text-xl font-medium">Bottom</span>
              </div>
            </IntuiResizablePanel>
          </IntuiResizablePanelGroup>
        </Subsection>

        <Subsection label="Three panels (nested)">
          <IntuiResizablePanelGroup orientation="horizontal" className="h-[300px] max-w-[480px]">
            <IntuiResizablePanel defaultSize={30}>
              <div className="flex h-full items-center justify-center">
                <span className="text-xl font-medium">Sidebar</span>
              </div>
            </IntuiResizablePanel>
            <IntuiResizableHandle />
            <IntuiResizablePanel defaultSize={70}>
              <IntuiResizablePanelGroup orientation="vertical">
                <IntuiResizablePanel defaultSize={60}>
                  <div className="flex h-full items-center justify-center">
                    <span className="text-xl font-medium">Content</span>
                  </div>
                </IntuiResizablePanel>
                <IntuiResizableHandle />
                <IntuiResizablePanel defaultSize={40}>
                  <div className="flex h-full items-center justify-center">
                    <span className="text-xl font-medium">Footer</span>
                  </div>
                </IntuiResizablePanel>
              </IntuiResizablePanelGroup>
            </IntuiResizablePanel>
          </IntuiResizablePanelGroup>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Navigation */}
      <Section title="Navigation">
        <Subsection label="Accordion">
          <div className="w-full max-w-md">
            <IntuiAccordion
              items={[
                { value: 'item-1', trigger: 'Is it accessible?', content: 'Yes. It adheres to the WAI-ARIA design pattern.' },
                { value: 'item-2', trigger: 'Is it styled?', content: 'Yes. It comes with default styles matching Figma specs.' },
                { value: 'item-3', trigger: 'Is it animated?', content: 'Yes. It uses CSS animations for smooth transitions.' },
              ]}
            />
          </div>
        </Subsection>
        <Subsection label="Breadcrumb">
          <IntuiBreadcrumb
            items={[
              { label: 'Home', href: '/' },
              { label: 'Components', href: '/revamp-components' },
              { label: 'Breadcrumb' },
            ]}
          />
        </Subsection>
        <Subsection label="Breadcrumb with truncation">
          <IntuiBreadcrumb
            maxItems={3}
            items={[
              { label: 'Home', href: '/' },
              { label: 'Projects', href: '/projects' },
              { label: 'Data Catalog', href: '/projects/catalog' },
              { label: 'Settings', href: '/projects/catalog/settings' },
              { label: 'Current Page' },
            ]}
          />
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Data Table */}
      <Section title="Data Table">
        <Subsection label="Basic Table with sorting & pagination">
          <div className="w-full">
            <IntuiTable
              columns={SAMPLE_TABLE_COLUMNS}
              data={SAMPLE_TABLE_DATA}
              getRowId={(row) => row.id}
              sortable
              pagination={{ pageSize: 3 }}
              caption="A list of recent invoices."
            />
          </div>
        </Subsection>
        <Subsection label="Table with selection">
          <div className="w-full">
            <IntuiTable
              columns={SAMPLE_TABLE_COLUMNS}
              data={SAMPLE_TABLE_DATA}
              getRowId={(row) => row.id}
              selectable
              selectionMode="multi"
            />
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Drawer */}
      <Section title="Drawer">
        <Subsection label="Directions">
          <IntuiDrawer title="Bottom Drawer" description="Slides up from the bottom." trigger={<IntuiButton variant="outline" size="sm">Bottom</IntuiButton>} direction="bottom"><p className="text-sm text-muted-foreground py-4">Drawer content goes here.</p></IntuiDrawer>
          <IntuiDrawer title="Left Drawer" description="Slides in from the left." trigger={<IntuiButton variant="outline" size="sm">Left</IntuiButton>} direction="left"><p className="text-sm text-muted-foreground py-4">Drawer content goes here.</p></IntuiDrawer>
          <IntuiDrawer title="Right Drawer" description="Slides in from the right." trigger={<IntuiButton variant="outline" size="sm">Right</IntuiButton>} direction="right"><p className="text-sm text-muted-foreground py-4">Drawer content goes here.</p></IntuiDrawer>
          <IntuiDrawer title="Top Drawer" description="Slides down from the top." trigger={<IntuiButton variant="outline" size="sm">Top</IntuiButton>} direction="top"><p className="text-sm text-muted-foreground py-4">Drawer content goes here.</p></IntuiDrawer>
        </Subsection>
        <Subsection label="With footer">
          <IntuiDrawer
            title="Confirm Action"
            description="Are you sure you want to proceed?"
            trigger={<IntuiButton variant="outline" size="sm">With Footer</IntuiButton>}
            footer={<div className="flex gap-2 justify-end"><IntuiButton variant="outline">Cancel</IntuiButton><IntuiButton>Confirm</IntuiButton></div>}
          >
            <p className="text-sm text-muted-foreground py-4">This drawer includes a footer with action buttons.</p>
          </IntuiDrawer>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Tabs */}
      <Section title="Tabs">
        <Subsection label="Default variant">
          <IntuiTabs defaultValue="account" className="w-[400px]">
            <IntuiTabsList>
              <IntuiTabsTrigger value="account">Account</IntuiTabsTrigger>
              <IntuiTabsTrigger value="password">Password</IntuiTabsTrigger>
              <IntuiTabsTrigger value="settings">Settings</IntuiTabsTrigger>
            </IntuiTabsList>
            <IntuiTabsContent value="account">
              <p className="text-sm text-muted-foreground">Make changes to your account here.</p>
            </IntuiTabsContent>
            <IntuiTabsContent value="password">
              <p className="text-sm text-muted-foreground">Change your password here.</p>
            </IntuiTabsContent>
            <IntuiTabsContent value="settings">
              <p className="text-sm text-muted-foreground">Adjust your settings here.</p>
            </IntuiTabsContent>
          </IntuiTabs>
        </Subsection>
        <Subsection label="Line variant">
          <IntuiTabs defaultValue="overview" className="w-[400px]">
            <IntuiTabsList variant="line">
              <IntuiTabsTrigger value="overview">Overview</IntuiTabsTrigger>
              <IntuiTabsTrigger value="analytics">Analytics</IntuiTabsTrigger>
              <IntuiTabsTrigger value="reports">Reports</IntuiTabsTrigger>
              <IntuiTabsTrigger value="disabled" disabled>Disabled</IntuiTabsTrigger>
            </IntuiTabsList>
            <IntuiTabsContent value="overview">
              <p className="text-sm text-muted-foreground">Overview content goes here.</p>
            </IntuiTabsContent>
            <IntuiTabsContent value="analytics">
              <p className="text-sm text-muted-foreground">Analytics data and charts.</p>
            </IntuiTabsContent>
            <IntuiTabsContent value="reports">
              <p className="text-sm text-muted-foreground">Generated reports.</p>
            </IntuiTabsContent>
          </IntuiTabs>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Calendar */}
      <Section title="Calendar">
        <Subsection label="Basic">
          <IntuiCalendar mode="single" />
        </Subsection>

        <Subsection label="Range (two months)">
          <IntuiCalendar mode="range" numberOfMonths={2} />
        </Subsection>

        <Subsection label="Month/Year Dropdown">
          <IntuiCalendar
            mode="single"
            captionLayout="dropdown"
            startMonth={CALENDAR_DROPDOWN_START}
            endMonth={CALENDAR_DROPDOWN_END}
          />
        </Subsection>

        <Subsection label="With Presets">
          <IntuiCalendarPresets />
        </Subsection>

        <Subsection label="Date and Time Picker">
          <CalendarDateTimeDemo />
        </Subsection>

        <Subsection label="Disabled dates (weekends)">
          <IntuiCalendar
            mode="single"
            disabled={{ dayOfWeek: [0, 6] }}
          />
        </Subsection>

        <Subsection label="With outside days hidden">
          <IntuiCalendar mode="single" showOutsideDays={false} />
        </Subsection>

        <Subsection label="Custom cell size (md)">
          <IntuiCalendar mode="single" size="md" />
        </Subsection>

        <Subsection label="Week numbers">
          <IntuiCalendar mode="single" showWeekNumber />
        </Subsection>

        <Subsection label="Range Calendar (3 months)">
          <IntuiCalendar mode="range" numberOfMonths={3} />
        </Subsection>

        <Subsection label="Custom Day Content (prices)">
          <IntuiCalendar
            mode="single"
            size="md"
            captionLayout="dropdown"
            startMonth={CALENDAR_DROPDOWN_START}
            endMonth={CALENDAR_DROPDOWN_END}
            renderDay={(_date, day) => (
              <div className="flex flex-col items-center leading-none">
                <span>{day}</span>
                <span className="text-[10px] text-muted-foreground">$100</span>
              </div>
            )}
          />
        </Subsection>

        <Subsection label="Mobile stacked (range)">
          <IntuiCalendar mode="range" numberOfMonths={2} mobile />
        </Subsection>

        <Subsection label="RTL / Persian-style">
          <IntuiCalendar mode="single" dir="rtl" />
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Sidebar */}
      <Section title="Sidebar">
        <Subsection label="Documentation sidebar (Figma Sidebar 01)">
          <div className="border rounded-lg overflow-hidden h-[600px]">
            <IntuiSidebar>
              <IntuiSidebarHeader>
                <IntuiSidebarHeaderButton
                  icon={<BookOpen />}
                  title="Documentation"
                  subtitle="v1.0.1"
                  collapseIcon={<ChevronsUpDown className="size-4" />}
                />
                <div className="px-2">
                  <input
                    placeholder="Search"
                    className="flex h-8 w-full rounded-lg border border-input bg-background px-2.5 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Getting started</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton>Installation</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton>Project Structure</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Building Your Application</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton>Routing</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton isActive>Data Fetching</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton>Rendering</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton>Caching</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton>Styling</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton>Testing</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>API Reference</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton>Components</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton>Functions</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton>CLI</IntuiSidebarMenuButton>
                    </IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
              <IntuiSidebarFooter>
                <IntuiSidebarMenuButton icon={<Settings />}>Settings</IntuiSidebarMenuButton>
              </IntuiSidebarFooter>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 02 — Collapsible groups (chevron-down)">
          <div className="border rounded-lg overflow-hidden h-[500px]">
            <IntuiSidebar>
              <IntuiSidebarHeader>
                <IntuiSidebarHeaderButton
                  icon={<BookOpen />}
                  title="Documentation"
                  subtitle="v1.0.1"
                  collapseIcon={<ChevronsUpDown className="size-4" />}
                />
                <div className="px-2">
                  <input placeholder="Search" className="flex h-8 w-full rounded-lg border border-input bg-background px-2.5 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                </div>
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Getting started <IntuiSidebarGroupAction><ChevronDown className="size-3" /></IntuiSidebarGroupAction></IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton>Installation</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton>Project Structure</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Building Your Application <IntuiSidebarGroupAction><ChevronDown className="size-3" /></IntuiSidebarGroupAction></IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton>Routing</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton isActive>Data Fetching</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton>Rendering</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton>Caching</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>API Reference <IntuiSidebarGroupAction><ChevronDown className="size-3" /></IntuiSidebarGroupAction></IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton>Components</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton>Functions</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 03 — Compact, no search, indented items">
          <div className="border rounded-lg overflow-hidden h-[500px]">
            <IntuiSidebar>
              <IntuiSidebarHeader>
                <IntuiSidebarHeaderButton
                  icon={<BookOpen />}
                  title="Documentation"
                  subtitle="v1.0.1"
                  collapseIcon={<ChevronsUpDown className="size-4" />}
                />
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Getting started</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Installation</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Project Structure</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Building Your Application</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Routing</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem isActive>Data Fetching</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Rendering</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Caching</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Styling</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>API Reference</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Components</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>File Conventions</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Functions</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 04 — BigIcon header, no search">
          <div className="border rounded-lg overflow-hidden h-[500px]">
            <IntuiSidebar>
              <IntuiSidebarHeader>
                <IntuiSidebarHeaderButton
                  icon={<BookOpen />}
                  title="Menu Item"
                  subtitle="Enterprise"
                  collapseIcon={<ChevronsUpDown className="size-4" />}
                />
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Getting started</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Installation</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Project Structure</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Building Your Application</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Routing</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem isActive>Data Fetching</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Rendering</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Caching</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 05 — Collapsible with minus icons">
          <div className="border rounded-lg overflow-hidden h-[500px]">
            <IntuiSidebar>
              <IntuiSidebarHeader>
                <IntuiSidebarHeaderButton
                  icon={<BookOpen />}
                  title="Documentation"
                  subtitle="v1.0.1"
                  collapseIcon={<ChevronsUpDown className="size-4" />}
                />
                <div className="px-2">
                  <input placeholder="Search" className="flex h-8 w-full rounded-lg border border-input bg-background px-2.5 py-1 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
                </div>
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Getting started <IntuiSidebarGroupAction><Minus className="size-3" /></IntuiSidebarGroupAction></IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Installation</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Project Structure</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Building Your Application <IntuiSidebarGroupAction><Minus className="size-3" /></IntuiSidebarGroupAction></IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Routing</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem isActive>Data Fetching</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Rendering</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Caching</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>API Reference <IntuiSidebarGroupAction><Minus className="size-3" /></IntuiSidebarGroupAction></IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Components</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Functions</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>CLI</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
            </IntuiSidebar>
          </div>
        </Subsection>
        <Subsection label="Sidebar 06 — Minimal with actions + newsletter footer">
          <div className="border rounded-lg overflow-hidden h-[500px]">
            <IntuiSidebar>
              <IntuiSidebarHeader>
                <IntuiSidebarHeaderButton icon={<BookOpen />} title="Documentation" subtitle="v1.0.1" collapseIcon={<ChevronsUpDown className="size-4" />} />
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Getting Started</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Building Your Application</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>API Reference</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Architecture</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
              <IntuiSidebarFooter>
                <div className="rounded-lg border bg-card p-4 space-y-2">
                  <p className="text-sm font-semibold">Subscribe to our newsletter</p>
                  <p className="text-xs text-muted-foreground">Opt-in to receive updates and news about the sidebar.</p>
                  <input placeholder="Email" className="flex h-8 w-full rounded-lg border border-input bg-background px-2.5 py-1 text-sm" />
                  <button className="flex h-8 w-full items-center justify-center rounded-lg bg-primary text-primary-foreground text-sm font-medium">Subscribe</button>
                </div>
              </IntuiSidebarFooter>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 07 — App sidebar with icons + collapsible tree">
          <div className="border rounded-lg overflow-hidden h-[500px]">
            <IntuiSidebar>
              <IntuiSidebarHeader>
                <IntuiSidebarHeaderButton icon={<BookOpen />} title="Documentation" subtitle="v1.0.1" collapseIcon={<ChevronsUpDown className="size-4" />} />
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Platform</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton icon={<SquareTerminal />} badge={<ChevronRight className="size-3" />}>Playground</IntuiSidebarMenuButton>
                      <IntuiSidebarMenuSub variant="indent">
                        <IntuiSidebarMenuSubItem>History</IntuiSidebarMenuSubItem>
                        <IntuiSidebarMenuSubItem>Starred</IntuiSidebarMenuSubItem>
                        <IntuiSidebarMenuSubItem>Settings</IntuiSidebarMenuSubItem>
                      </IntuiSidebarMenuSub>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Bot />} badge={<ChevronRight className="size-3" />}>Models</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<BookOpen />} badge={<ChevronRight className="size-3" />}>Documentation</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Settings />} badge={<ChevronRight className="size-3" />}>Settings</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Projects</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Hash />}>Design Engineering</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Globe />}>Sales & Marketing</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<LayoutDashboard />}>Travel</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<MoreHorizontal />}>More</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 08 — Inbox with items">
          <div className="border rounded-lg overflow-hidden h-[600px]">
            <IntuiSidebar className="w-[350px]">
              <IntuiSidebarHeader>
                <div className="flex items-center justify-between p-2">
                  <span className="text-lg font-semibold">Inbox</span>
                  <span className="text-sm text-muted-foreground">Unreads</span>
                </div>
                <div className="px-2">
                  <input placeholder="Search" className="flex h-8 w-full rounded-lg border border-input bg-background px-2.5 py-1 text-sm placeholder:text-muted-foreground" />
                </div>
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <div className="flex flex-col">
                  <IntuiSidebarInboxItem sender="William Smith" subject="Meeting Tomorrow" preview="Lorem ipsum dolor sit amet, consectetur adipiscing elit interdum hendrerit ex vitae sodal..." timestamp="09:34 AM" />
                  <IntuiSidebarInboxItem sender="Sarah Johnson" subject="Project Update" preview="Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia Curae; Maece..." timestamp="10:45 AM" />
                  <IntuiSidebarInboxItem sender="Michael Brown" subject="Client Presentation" preview="Fusce vitae laoreet enim, id auctor justo. Donec fermentum ligula sed dolor sollicitudin congue." timestamp="11:30 AM" />
                  <IntuiSidebarInboxItem sender="Emily Davis" subject="Team Brainstorming" preview="Pellentesque habitant morbi tristique senectus et netus et malesuada fames ac turpis egestas...." timestamp="02:15 PM" />
                  <IntuiSidebarInboxItem sender="Daniel Wilson" subject="Product Design Review" preview="Suspendisse varius leo non neque molestie, eget fermentum elit tincidunt..." timestamp="03:45 PM" />
                </div>
              </IntuiSidebarContent>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 09 — Mail categories">
          <div className="border rounded-lg overflow-hidden h-[400px]">
            <IntuiSidebar>
              <IntuiSidebarHeader>
                <IntuiSidebarHeaderButton icon={<BookOpen />} title="Acme Inc" subtitle="Enterprise" collapseIcon={<ChevronsUpDown className="size-4" />} />
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Archive />} isActive>Inbox</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<FileText />}>Drafts</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Send />}>Sent</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Archive />}>Junk</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<TrashIcon />}>Trash</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 10 — Notion-style with emoji icons">
          <div className="border rounded-lg overflow-hidden h-[600px]">
            <IntuiSidebar>
              <IntuiSidebarHeader>
                <IntuiSidebarHeaderButton icon={<Sparkles />} title="Acme Inc" collapseIcon={<ChevronDown className="size-4" />} />
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Search />}>Search</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Sparkles />}>Ask AI</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Home />} isActive>Home</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Inbox />}>Inbox</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Group title</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Project Management</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Family Recipe Collection</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Fitness Tracker</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Book Notes</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<MoreHorizontal />}>More</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Group title</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Personal Life Management</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Professional Development</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Creative Projects</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton badge={<MoreHorizontal className="size-4 text-muted-foreground" />}>Home Management</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<MoreHorizontal />}>More</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
              <IntuiSidebarFooter>
                <IntuiSidebarMenu>
                  <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Calendar />}>Calendar</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Settings />}>Settings</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<FileText />}>Templates</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<TrashIcon />}>Trash</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<HelpCircle />}>Help</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                </IntuiSidebarMenu>
              </IntuiSidebarFooter>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 11 — File tree explorer">
          <div className="border rounded-lg overflow-hidden h-[500px]">
            <IntuiSidebar>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Changes</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<File />} badge={<span className="text-xs text-muted-foreground">M</span>}>README.md</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<File />} badge={<span className="text-xs text-muted-foreground">U</span>}>api/hello/route.ts</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<File />} badge={<span className="text-xs text-muted-foreground">M</span>}>app/layout.tsx</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Files</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton icon={<Folder />} badge={<ChevronDown className="size-3" />}>app</IntuiSidebarMenuButton>
                      <IntuiSidebarMenuSub variant="border">
                        <IntuiSidebarMenuSubItem>api</IntuiSidebarMenuSubItem>
                        <IntuiSidebarMenuSubItem>page.tsx</IntuiSidebarMenuSubItem>
                        <IntuiSidebarMenuSubItem>layout.tsx</IntuiSidebarMenuSubItem>
                      </IntuiSidebarMenuSub>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem>
                      <IntuiSidebarMenuButton icon={<Folder />} badge={<ChevronDown className="size-3" />}>components</IntuiSidebarMenuButton>
                      <IntuiSidebarMenuSub variant="border">
                        <IntuiSidebarMenuSubItem>button.tsx</IntuiSidebarMenuSubItem>
                        <IntuiSidebarMenuSubItem>card.ts</IntuiSidebarMenuSubItem>
                      </IntuiSidebarMenuSub>
                    </IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<File />}>.eslintrc.json</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<File />}>README.md</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 12 — Calendar sidebar with checkboxes">
          <div className="border rounded-lg overflow-hidden h-[600px]">
            <IntuiSidebar>
              <IntuiSidebarHeader>
                <IntuiSidebarHeaderButton icon={<Calendar />} title="shadcn" subtitle="m@example.com" collapseIcon={<ChevronsUpDown className="size-4" />} />
              </IntuiSidebarHeader>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <div className="px-2">
                    <IntuiCalendar mode="single" bare className="p-0" captionLayout="dropdown" startMonth={CALENDAR_DROPDOWN_START} endMonth={CALENDAR_DROPDOWN_END} />
                  </div>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>My Calendars <IntuiSidebarGroupAction><ChevronDown className="size-3" /></IntuiSidebarGroupAction></IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<CheckSquare />}>Personal</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<CheckSquare />}>Work</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton>Family</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Favorites <IntuiSidebarGroupAction><ChevronDown className="size-3" /></IntuiSidebarGroupAction></IntuiSidebarGroupLabel>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<CheckSquare />}>Holidays</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<CheckSquare />}>Birthdays</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
              <IntuiSidebarFooter>
                <IntuiSidebarMenuButton icon={<Plus />}>New Calendar</IntuiSidebarMenuButton>
              </IntuiSidebarFooter>
            </IntuiSidebar>
          </div>
        </Subsection>

        <Subsection label="Sidebar 13 — Settings page with sidebar nav">
          <div className="border rounded-lg overflow-hidden h-[400px] flex">
            <IntuiSidebar className="w-[200px] border-r">
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarMenu>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Bell />}>Notifications</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Globe />}>Navigation</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Home />}>Home</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Paintbrush />}>Appearance</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<MessageCircle />} isActive>Messages & media</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Languages />}>Language & region</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Accessibility />}>Accessibility</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Check />}>Mark as read</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Monitor />}>Audio & video</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Link />}>Connected accounts</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Lock />}>Privacy & visibility</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                    <IntuiSidebarMenuItem><IntuiSidebarMenuButton icon={<Settings />}>Advanced</IntuiSidebarMenuButton></IntuiSidebarMenuItem>
                  </IntuiSidebarMenu>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
            </IntuiSidebar>
            <div className="flex-1 p-6">
              <div className="text-sm text-muted-foreground">Settings &gt; <span className="text-foreground font-medium">Messages & media</span></div>
              <div className="mt-4 flex items-center justify-center h-[300px] rounded-lg border-2 border-dashed text-sm text-muted-foreground">
                Content area
              </div>
            </div>
          </div>
        </Subsection>

        <Subsection label="Sidebar 14 — Table of Contents">
          <div className="border rounded-lg overflow-hidden h-[500px]">
            <IntuiSidebar>
              <IntuiSidebarContent>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel>Table of Contents</IntuiSidebarGroupLabel>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel className="font-semibold text-sidebar-foreground opacity-100">Getting started</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Installation</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Project Structure</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel className="font-semibold text-sidebar-foreground opacity-100">Building Your Application</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Routing</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem isActive>Data Fetching</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Rendering</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Caching</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Styling</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Optimizing</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Configuring</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Testing</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Authentication</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Deploying</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Upgrading</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Examples</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel className="font-semibold text-sidebar-foreground opacity-100">API Reference</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Components</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>File Conventions</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Functions</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>next.config.js Options</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>CLI</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Edge Runtime</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
                <IntuiSidebarGroup>
                  <IntuiSidebarGroupLabel className="font-semibold text-sidebar-foreground opacity-100">Architecture</IntuiSidebarGroupLabel>
                  <IntuiSidebarMenuSub variant="indent">
                    <IntuiSidebarMenuSubItem>Accessibility</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Fast Refresh</IntuiSidebarMenuSubItem>
                    <IntuiSidebarMenuSubItem>Next.js Compiler</IntuiSidebarMenuSubItem>
                  </IntuiSidebarMenuSub>
                </IntuiSidebarGroup>
              </IntuiSidebarContent>
            </IntuiSidebar>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Navigation Menu */}
      <Section title="Navigation Menu">
        <Subsection label="Full navigation with dropdown panels">
          <IntuiNavigationMenu>
            <IntuiNavigationMenuList>
              <IntuiNavigationMenuItem>
                <IntuiNavigationMenuTrigger>Getting Started</IntuiNavigationMenuTrigger>
                <IntuiNavigationMenuContent>
                  <ul className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                    <li className="row-span-3">
                      <IntuiNavigationMenuLink asChild>
                        <a className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md" href="#">
                          <div className="mb-2 mt-4 text-lg font-medium">shadcn/ui</div>
                          <p className="text-sm leading-tight text-muted-foreground">Beautifully designed components built with Radix UI and Tailwind CSS.</p>
                        </a>
                      </IntuiNavigationMenuLink>
                    </li>
                    <IntuiNavigationMenuListItem href="#" title="Introduction">Re-usable components built using Radix UI and Tailwind CSS.</IntuiNavigationMenuListItem>
                    <IntuiNavigationMenuListItem href="#" title="Installation">How to install dependencies and structure your app.</IntuiNavigationMenuListItem>
                    <IntuiNavigationMenuListItem href="#" title="Typography">Styles for headings, paragraphs, lists, etc.</IntuiNavigationMenuListItem>
                  </ul>
                </IntuiNavigationMenuContent>
              </IntuiNavigationMenuItem>
              <IntuiNavigationMenuItem>
                <IntuiNavigationMenuTrigger>Components</IntuiNavigationMenuTrigger>
                <IntuiNavigationMenuContent>
                  <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2">
                    <IntuiNavigationMenuListItem href="#" title="Alert Dialog">A modal dialog that interrupts the user.</IntuiNavigationMenuListItem>
                    <IntuiNavigationMenuListItem href="#" title="Hover Card">For previewing content behind a link.</IntuiNavigationMenuListItem>
                    <IntuiNavigationMenuListItem href="#" title="Progress">Displays an indicator showing completion.</IntuiNavigationMenuListItem>
                    <IntuiNavigationMenuListItem href="#" title="Scroll-area">Visually or semantically separates content.</IntuiNavigationMenuListItem>
                    <IntuiNavigationMenuListItem href="#" title="Tabs">A set of layered sections of content.</IntuiNavigationMenuListItem>
                    <IntuiNavigationMenuListItem href="#" title="Tooltip">A popup that displays on hover.</IntuiNavigationMenuListItem>
                  </ul>
                </IntuiNavigationMenuContent>
              </IntuiNavigationMenuItem>
              <IntuiNavigationMenuItem>
                <IntuiNavigationMenuLink className={navigationMenuTriggerStyle()} href="#">
                  Documentation
                </IntuiNavigationMenuLink>
              </IntuiNavigationMenuItem>
            </IntuiNavigationMenuList>
          </IntuiNavigationMenu>
        </Subsection>

        <Subsection label="Simple list dropdown">
          <IntuiNavigationMenu>
            <IntuiNavigationMenuList>
              <IntuiNavigationMenuItem>
                <IntuiNavigationMenuTrigger>Item 1</IntuiNavigationMenuTrigger>
                <IntuiNavigationMenuContent>
                  <ul className="grid w-[200px] gap-1 p-2">
                    <IntuiNavigationMenuListItem href="#" title="Components" />
                    <IntuiNavigationMenuListItem href="#" title="Introduction" />
                    <IntuiNavigationMenuListItem href="#" title="Docs" />
                  </ul>
                </IntuiNavigationMenuContent>
              </IntuiNavigationMenuItem>
              <IntuiNavigationMenuItem>
                <IntuiNavigationMenuTrigger>Simple List</IntuiNavigationMenuTrigger>
                <IntuiNavigationMenuContent>
                  <ul className="grid w-[200px] gap-1 p-2">
                    <IntuiNavigationMenuListItem href="#" title="Components" />
                    <IntuiNavigationMenuListItem href="#" title="Documentation" />
                    <IntuiNavigationMenuListItem href="#" title="Music" />
                  </ul>
                </IntuiNavigationMenuContent>
              </IntuiNavigationMenuItem>
              <IntuiNavigationMenuItem>
                <IntuiNavigationMenuTrigger>With Icons</IntuiNavigationMenuTrigger>
                <IntuiNavigationMenuContent>
                  <ul className="grid w-[200px] gap-1 p-2">
                    <IntuiNavigationMenuListItem href="#" title="Desktop" icon={<Monitor />} />
                    <IntuiNavigationMenuListItem href="#" title="Mobile" icon={<Sparkles />} />
                    <IntuiNavigationMenuListItem href="#" title="Zoom" icon={<Search />} />
                  </ul>
                </IntuiNavigationMenuContent>
              </IntuiNavigationMenuItem>
            </IntuiNavigationMenuList>
          </IntuiNavigationMenu>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Menubar */}
      <Section title="Menubar">
        <Subsection label="Full menubar (File, Edit, View, Profiles)">
          <IntuiMenubar>
            <IntuiMenubarMenu>
              <IntuiMenubarTrigger>File</IntuiMenubarTrigger>
              <IntuiMenubarContent>
                <IntuiMenubarItem shortcut="⌘N">New Tab</IntuiMenubarItem>
                <IntuiMenubarItem shortcut="⌘W">New Window</IntuiMenubarItem>
                <IntuiMenubarItem>New Incognito Window</IntuiMenubarItem>
                <IntuiMenubarSeparator />
                <IntuiMenubarSub>
                  <IntuiMenubarSubTrigger>Share</IntuiMenubarSubTrigger>
                  <IntuiMenubarSubContent>
                    <IntuiMenubarItem>Email link</IntuiMenubarItem>
                    <IntuiMenubarItem>Messages</IntuiMenubarItem>
                    <IntuiMenubarItem>Notes</IntuiMenubarItem>
                  </IntuiMenubarSubContent>
                </IntuiMenubarSub>
                <IntuiMenubarSeparator />
                <IntuiMenubarItem shortcut="⌘P">Print...</IntuiMenubarItem>
              </IntuiMenubarContent>
            </IntuiMenubarMenu>
            <IntuiMenubarMenu>
              <IntuiMenubarTrigger>Edit</IntuiMenubarTrigger>
              <IntuiMenubarContent>
                <IntuiMenubarItem shortcut="⌘Z">Undo</IntuiMenubarItem>
                <IntuiMenubarItem shortcut="⇧⌘Z">Redo</IntuiMenubarItem>
                <IntuiMenubarSeparator />
                <IntuiMenubarSub>
                  <IntuiMenubarSubTrigger>Find</IntuiMenubarSubTrigger>
                  <IntuiMenubarSubContent>
                    <IntuiMenubarItem>Search the web</IntuiMenubarItem>
                    <IntuiMenubarSeparator />
                    <IntuiMenubarItem>Find...</IntuiMenubarItem>
                    <IntuiMenubarItem>Find Next</IntuiMenubarItem>
                    <IntuiMenubarItem>Find Previous</IntuiMenubarItem>
                  </IntuiMenubarSubContent>
                </IntuiMenubarSub>
                <IntuiMenubarSeparator />
                <IntuiMenubarItem>Cut</IntuiMenubarItem>
                <IntuiMenubarItem>Copy</IntuiMenubarItem>
                <IntuiMenubarItem>Paste</IntuiMenubarItem>
              </IntuiMenubarContent>
            </IntuiMenubarMenu>
            <IntuiMenubarMenu>
              <IntuiMenubarTrigger>View</IntuiMenubarTrigger>
              <IntuiMenubarContent>
                <IntuiMenubarCheckboxItem>Always Show Bookmarks Bar</IntuiMenubarCheckboxItem>
                <IntuiMenubarCheckboxItem checked>Always Show Full URLs</IntuiMenubarCheckboxItem>
                <IntuiMenubarSeparator />
                <IntuiMenubarItem inset shortcut="⌘R">Reload</IntuiMenubarItem>
                <IntuiMenubarItem inset shortcut="⇧⌘R" disabled>Force Reload</IntuiMenubarItem>
                <IntuiMenubarSeparator />
                <IntuiMenubarItem inset>Toggle Fullscreen</IntuiMenubarItem>
                <IntuiMenubarSeparator />
                <IntuiMenubarItem inset>Hide Sidebar</IntuiMenubarItem>
              </IntuiMenubarContent>
            </IntuiMenubarMenu>
            <IntuiMenubarMenu>
              <IntuiMenubarTrigger>Profiles</IntuiMenubarTrigger>
              <IntuiMenubarContent>
                <IntuiMenubarRadioGroup value="benoit">
                  <IntuiMenubarRadioItem value="benoit">Benoit</IntuiMenubarRadioItem>
                  <IntuiMenubarRadioItem value="andy">Andy</IntuiMenubarRadioItem>
                  <IntuiMenubarRadioItem value="luis">Luis</IntuiMenubarRadioItem>
                </IntuiMenubarRadioGroup>
                <IntuiMenubarSeparator />
                <IntuiMenubarItem inset>Edit...</IntuiMenubarItem>
                <IntuiMenubarSeparator />
                <IntuiMenubarItem inset>Add Profile...</IntuiMenubarItem>
              </IntuiMenubarContent>
            </IntuiMenubarMenu>
          </IntuiMenubar>
        </Subsection>

        <Subsection label="With destructive items">
          <IntuiMenubar>
            <IntuiMenubarMenu>
              <IntuiMenubarTrigger>File</IntuiMenubarTrigger>
              <IntuiMenubarContent>
                <IntuiMenubarLabel>Actions</IntuiMenubarLabel>
                <IntuiMenubarItem><Settings />Settings</IntuiMenubarItem>
                <IntuiMenubarItem><HelpCircle />Help</IntuiMenubarItem>
                <IntuiMenubarSeparator />
                <IntuiMenubarItem variant="destructive"><TrashIcon />Delete</IntuiMenubarItem>
              </IntuiMenubarContent>
            </IntuiMenubarMenu>
            <IntuiMenubarMenu>
              <IntuiMenubarTrigger>More</IntuiMenubarTrigger>
              <IntuiMenubarContent>
                <IntuiMenubarItem>About</IntuiMenubarItem>
              </IntuiMenubarContent>
            </IntuiMenubarMenu>
          </IntuiMenubar>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Select */}
      <Section title="Select">
        <Subsection label="Basic select">
          <IntuiSelect>
            <IntuiSelectTrigger className="w-[200px]">
              <IntuiSelectValue placeholder="Select a fruit" />
            </IntuiSelectTrigger>
            <IntuiSelectContent>
              <IntuiSelectItem value="apple">Apple</IntuiSelectItem>
              <IntuiSelectItem value="banana">Banana</IntuiSelectItem>
              <IntuiSelectItem value="cherry">Cherry</IntuiSelectItem>
              <IntuiSelectSeparator />
              <IntuiSelectItem value="grape">Grape</IntuiSelectItem>
              <IntuiSelectItem value="orange">Orange</IntuiSelectItem>
            </IntuiSelectContent>
          </IntuiSelect>
        </Subsection>
        <Subsection label="Invalid state">
          <IntuiSelect>
            <IntuiSelectTrigger className="w-[200px]" invalid>
              <IntuiSelectValue placeholder="Required field" />
            </IntuiSelectTrigger>
            <IntuiSelectContent>
              <IntuiSelectItem value="opt1">Option 1</IntuiSelectItem>
              <IntuiSelectItem value="opt2">Option 2</IntuiSelectItem>
            </IntuiSelectContent>
          </IntuiSelect>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Combobox */}
      <Section title="Combobox">
        <Subsection label="Searchable select">
          <IntuiCombobox open={comboboxOpen} onOpenChange={setComboboxOpen}>
            <IntuiComboboxTrigger className="w-[200px]">
              {comboboxValue
                ? ['React', 'Vue', 'Angular', 'Svelte', 'Solid'].find(
                    (f) => f.toLowerCase() === comboboxValue
                  )
                : 'Select framework...'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </IntuiComboboxTrigger>
            <IntuiComboboxContent>
              <IntuiComboboxInput placeholder="Search framework..." />
              <IntuiComboboxList>
                <IntuiComboboxEmpty>No framework found.</IntuiComboboxEmpty>
                {['React', 'Vue', 'Angular', 'Svelte', 'Solid'].map((fw) => (
                  <IntuiComboboxItem
                    key={fw}
                    value={fw.toLowerCase()}
                    selected={comboboxValue === fw.toLowerCase()}
                    onSelect={(val) => {
                      setComboboxValue(val === comboboxValue ? '' : val)
                      setComboboxOpen(false)
                    }}
                  >
                    {fw}
                    {comboboxValue === fw.toLowerCase() && <Check className="ml-auto h-4 w-4" />}
                  </IntuiComboboxItem>
                ))}
              </IntuiComboboxList>
            </IntuiComboboxContent>
          </IntuiCombobox>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Alert Dialog */}
      <Section title="Alert Dialog">
        <Subsection label="Destructive: No (primary action)">
          <IntuiAlertDialog>
            <IntuiAlertDialogTrigger asChild>
              <IntuiButton variant="outline">Confirm Action</IntuiButton>
            </IntuiAlertDialogTrigger>
            <IntuiAlertDialogContent>
              <IntuiAlertDialogHeader>
                <IntuiAlertDialogTitle>Are you absolutely sure?</IntuiAlertDialogTitle>
                <IntuiAlertDialogDescription>
                  This action cannot be undone. This will permanently delete your account from our servers.
                </IntuiAlertDialogDescription>
              </IntuiAlertDialogHeader>
              <IntuiAlertDialogFooter>
                <IntuiAlertDialogCancel>Cancel</IntuiAlertDialogCancel>
                <IntuiAlertDialogAction>Continue</IntuiAlertDialogAction>
              </IntuiAlertDialogFooter>
            </IntuiAlertDialogContent>
          </IntuiAlertDialog>
        </Subsection>
        <Subsection label="Destructive: Yes (destructive action)">
          <IntuiAlertDialog>
            <IntuiAlertDialogTrigger asChild>
              <IntuiButton variant="destructive">Delete Account</IntuiButton>
            </IntuiAlertDialogTrigger>
            <IntuiAlertDialogContent>
              <IntuiAlertDialogHeader>
                <IntuiAlertDialogTitle>Are you absolutely sure?</IntuiAlertDialogTitle>
                <IntuiAlertDialogDescription>
                  This action cannot be undone. This will permanently delete your account and remove your data from our servers.
                </IntuiAlertDialogDescription>
              </IntuiAlertDialogHeader>
              <IntuiAlertDialogFooter>
                <IntuiAlertDialogCancel>Cancel</IntuiAlertDialogCancel>
                <IntuiAlertDialogAction variant="destructive">Continue</IntuiAlertDialogAction>
              </IntuiAlertDialogFooter>
            </IntuiAlertDialogContent>
          </IntuiAlertDialog>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Card */}
      <Section title="Card">
        <Subsection label="Login card">
          <IntuiCard className="w-[420px]">
            <IntuiCardHeader>
              <IntuiCardTitle>Login</IntuiCardTitle>
              <IntuiCardDescription>Enter your credentials to access your account.</IntuiCardDescription>
            </IntuiCardHeader>
            <IntuiCardContent>
              <div className="grid gap-4">
                <div className="space-y-2"><IntuiLabel htmlFor="p1-card-email">Email</IntuiLabel><IntuiInput id="p1-card-email" type="email" placeholder="you@example.com" /></div>
                <div className="space-y-2"><IntuiLabel htmlFor="p1-card-password">Password</IntuiLabel><IntuiInput id="p1-card-password" type="password" placeholder="••••••••" /></div>
              </div>
            </IntuiCardContent>
            <IntuiCardFooter><div className="flex gap-2 justify-end w-full"><IntuiButton variant="outline">Cancel</IntuiButton><IntuiButton>Sign in</IntuiButton></div></IntuiCardFooter>
          </IntuiCard>
        </Subsection>
        <Subsection label="Card with content">
          <IntuiCard className="w-[420px]">
            <IntuiCardHeader>
              <IntuiCardTitle>Title Text</IntuiCardTitle>
              <IntuiCardDescription>This is a card description.</IntuiCardDescription>
            </IntuiCardHeader>
            <IntuiCardContent>
              <p className="text-sm text-muted-foreground">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Vivamus interdum hendrerit ex vitae sodales. Donec id leo ipsum.</p>
            </IntuiCardContent>
            <IntuiCardFooter><IntuiButton variant="outline" size="sm">Cancel</IntuiButton></IntuiCardFooter>
          </IntuiCard>
        </Subsection>
        <Subsection label="Notifications (default)">
          <IntuiCard className="w-[350px]">
            <IntuiCardHeader>
              <IntuiCardTitle>Notifications</IntuiCardTitle>
              <IntuiCardDescription>You have 3 unread messages.</IntuiCardDescription>
            </IntuiCardHeader>
            <IntuiCardContent>
              <p className="text-sm">Your call has been confirmed.</p>
              <p className="text-sm text-muted-foreground">1 hour ago</p>
            </IntuiCardContent>
            <IntuiCardFooter>
              <IntuiButton variant="outline" size="sm">Mark all as read</IntuiButton>
            </IntuiCardFooter>
          </IntuiCard>
        </Subsection>
        <Subsection label="Small size">
          <IntuiCard size="sm" className="w-[300px]">
            <IntuiCardHeader>
              <IntuiCardTitle>Quick Stats</IntuiCardTitle>
              <IntuiCardDescription>Last 7 days</IntuiCardDescription>
            </IntuiCardHeader>
            <IntuiCardContent>
              <p className="text-2xl font-bold">1,234</p>
              <p className="text-xs text-muted-foreground">+12% from last week</p>
            </IntuiCardContent>
          </IntuiCard>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Collapsible */}
      <Section title="Collapsible">
        <Subsection label="Controlled">
          <IntuiCollapsible open={collapsibleOpen} onOpenChange={setCollapsibleOpen} className="w-[350px] space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">@peduarte starred 3 repositories</span>
              <IntuiCollapsibleTrigger asChild>
                <IntuiButton variant="ghost" size="sm">
                  <ChevronsUpDown className="h-4 w-4" />
                </IntuiButton>
              </IntuiCollapsibleTrigger>
            </div>
            <div className="rounded-md border px-4 py-3 text-sm">@radix-ui/primitives</div>
            <IntuiCollapsibleContent className="space-y-2">
              <div className="rounded-md border px-4 py-3 text-sm">@radix-ui/colors</div>
              <div className="rounded-md border px-4 py-3 text-sm">@stitches/react</div>
            </IntuiCollapsibleContent>
          </IntuiCollapsible>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Hover Card */}
      <Section title="Hover Card">
        <Subsection label="Default trigger (hover to open)">
          <IntuiHoverCard>
            <IntuiHoverCardTrigger>@nextjs</IntuiHoverCardTrigger>
            <IntuiHoverCardContent className="w-80">
              <div className="flex gap-4">
                <IntuiAvatar fallback="N" size="lg" />
                <div className="space-y-1">
                  <h4 className="text-sm font-semibold">@nextjs</h4>
                  <p className="text-sm text-muted-foreground">
                    The React Framework — created and maintained by @vercel.
                  </p>
                  <div className="flex items-center pt-2">
                    <CalendarDays className="mr-2 h-4 w-4 opacity-70" />
                    <span className="text-xs text-muted-foreground">Joined December 2021</span>
                  </div>
                </div>
              </div>
            </IntuiHoverCardContent>
          </IntuiHoverCard>
        </Subsection>
        <Subsection label="Trigger states">
          <div className="flex flex-col gap-2 w-[250px]">
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground w-16">Default</span>
              <IntuiHoverCard>
                <IntuiHoverCardTrigger>Trigger Text</IntuiHoverCardTrigger>
                <IntuiHoverCardContent><p className="text-sm">Default state card</p></IntuiHoverCardContent>
              </IntuiHoverCard>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground w-16">Hover</span>
              <span className="text-sm text-muted-foreground italic">Hover over any trigger above</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground w-16">Focus</span>
              <span className="text-sm text-muted-foreground italic">Tab to any trigger to see focus ring</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground w-16">Disabled</span>
              <IntuiHoverCard>
                <IntuiHoverCardTrigger data-disabled="" aria-disabled>Trigger Text</IntuiHoverCardTrigger>
                <IntuiHoverCardContent><p className="text-sm">Won't open</p></IntuiHoverCardContent>
              </IntuiHoverCard>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-xs text-muted-foreground w-16">Pressed</span>
              <span className="text-sm text-muted-foreground italic">Click and hold any trigger</span>
            </div>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Popover */}
      <Section title="Popover">
        <Subsection label="Settings popover">
          <IntuiPopover>
            <IntuiPopoverTrigger asChild>
              <IntuiButton variant="outline">Open Popover</IntuiButton>
            </IntuiPopoverTrigger>
            <IntuiPopoverContent className="w-80">
              <div className="grid gap-4">
                <div className="space-y-2">
                  <h4 className="font-medium leading-none">Dimensions</h4>
                  <p className="text-sm text-muted-foreground">Set the dimensions for the layer.</p>
                </div>
                <div className="grid gap-2">
                  <div className="grid grid-cols-3 items-center gap-4">
                    <IntuiLabel>Width</IntuiLabel>
                    <IntuiInput defaultValue="100%" className="col-span-2 h-8" />
                  </div>
                  <div className="grid grid-cols-3 items-center gap-4">
                    <IntuiLabel>Height</IntuiLabel>
                    <IntuiInput defaultValue="25px" className="col-span-2 h-8" />
                  </div>
                </div>
              </div>
            </IntuiPopoverContent>
          </IntuiPopover>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Radio Group */}
      <Section title="Radio Group (Intui)">
        <Subsection label="Default (with labels and descriptions)">
          <IntuiRadioGroup value={intuiRadioValue} onValueChange={setIntuiRadioValue} className="w-[350px]">
            <IntuiRadioGroupItem value="default" label="Default" description="The default system spacing." />
            <IntuiRadioGroupItem value="comfortable" label="Comfortable" description="More space between items." />
            <IntuiRadioGroupItem value="compact" label="Compact" description="Tighter spacing for dense UIs." />
          </IntuiRadioGroup>
        </Subsection>
        <Subsection label="Disabled">
          <IntuiRadioGroup defaultValue="opt1">
            <IntuiRadioGroupItem value="opt1" label="Selected (disabled)" description="This option is selected but disabled." disabled />
            <IntuiRadioGroupItem value="opt2" label="Unselected (disabled)" description="This option is disabled." disabled />
          </IntuiRadioGroup>
        </Subsection>
        <Subsection label="Invalid">
          <IntuiRadioGroup defaultValue="opt1">
            <IntuiRadioGroupItem value="opt1" label="Selected (invalid)" description="This is a radio description." invalid />
            <IntuiRadioGroupItem value="opt2" label="Unselected (invalid)" description="This is a radio description." invalid />
          </IntuiRadioGroup>
        </Subsection>
        <Subsection label="Horizontal">
          <IntuiRadioGroup defaultValue="opt1" orientation="horizontal">
            <IntuiRadioGroupItem value="opt1" label="Option A" />
            <IntuiRadioGroupItem value="opt2" label="Option B" />
            <IntuiRadioGroupItem value="opt3" label="Option C" disabled />
          </IntuiRadioGroup>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Scroll Area */}
      <Section title="Scroll Area">
        <Subsection label="Vertical scroll">
          <IntuiScrollArea className="h-[200px] w-[250px] rounded-md border p-4">
            <div className="space-y-4">
              {Array.from({ length: 20 }, (_, i) => (
                <div key={i} className="text-sm">
                  Item {i + 1} — Lorem ipsum dolor sit amet
                </div>
              ))}
            </div>
          </IntuiScrollArea>
        </Subsection>
        <Subsection label="Horizontal scroll">
          <IntuiScrollArea orientation="horizontal" className="w-[400px] rounded-md border p-4">
            <div className="flex gap-4">
              {Array.from({ length: 15 }, (_, i) => (
                <div key={i} className="flex-shrink-0 w-[150px] h-[100px] rounded-md bg-muted flex items-center justify-center text-sm">
                  Card {i + 1}
                </div>
              ))}
            </div>
          </IntuiScrollArea>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Toast (Sonner) */}
      <Section title="Toast (Sonner)">
        <IntuiToaster />
        <Subsection label="Default">
          <IntuiButton variant="outline" onClick={() => toast('Event has been created')}>Default</IntuiButton>
          <IntuiButton variant="outline" onClick={() => toast('Event has been created', { description: 'Monday, January 3rd at 6:00pm' })}>With Description</IntuiButton>
        </Subsection>
        <Subsection label="Icon variants">
          <div className="flex flex-wrap gap-2">
            <IntuiButton variant="outline" onClick={() => toast.success('Event has been created')}>Success</IntuiButton>
            <IntuiButton variant="outline" onClick={() => toast.info('Be at the area 10 minutes before the event time')}>Info</IntuiButton>
            <IntuiButton variant="outline" onClick={() => toast.warning('Event start time cannot be earlier than 8am')}>Warning</IntuiButton>
            <IntuiButton variant="outline" onClick={() => toast.error('Event has not been created')}>Error</IntuiButton>
            <IntuiButton variant="outline" onClick={() => {const id=toast.loading('Loading');setTimeout(()=>toast.dismiss(id),3000)}}>Loading</IntuiButton>
          </div>
        </Subsection>
        <Subsection label="With actions">
          <IntuiButton variant="outline" onClick={() => toast('Event has been created', { action: { label: 'Undo', onClick: () => {} } })}>Action (Undo)</IntuiButton>
          <IntuiButton variant="outline" onClick={() => toast('Event has been created', { cancel: { label: 'Cancel', onClick: () => {} } })}>Cancel</IntuiButton>
          <IntuiButton variant="outline" onClick={() => toast('Event has been created', { description: 'Sunday, December 03, 2023 at 9:00 AM', action: { label: 'Undo', onClick: () => {} } })}>Description + Action</IntuiButton>
        </Subsection>
        <Subsection label="Promise">
          <IntuiButton variant="outline" onClick={() => toast.promise(new Promise((r) => setTimeout(r, 2000)), { loading: 'Saving...', success: 'Settings saved!', error: 'Could not save.' })}>Promise</IntuiButton>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Textarea */}
      <Section title="Textarea">
        <Subsection label="Default">
          <IntuiTextarea placeholder="Type your message..." className="w-[350px]" />
        </Subsection>
        <Subsection label="Auto-resize with character count">
          <IntuiTextarea
            placeholder="Start typing and I'll grow..."
            autoResize
            minRows={2}
            maxRows={6}
            showCount
            maxLength={500}
            value={textareaValue}
            onChange={(e) => setTextareaValue(e.target.value)}
            className="w-[350px]"
          />
        </Subsection>
        <Subsection label="Error variant">
          <IntuiTextarea placeholder="Invalid input" variant="error" className="w-[350px]" />
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Pagination */}
      <Section title="Pagination">
        <Subsection label="Interactive pagination">
          <div className="space-y-2">
            <IntuiPagination page={paginationPage} totalPages={20} onPageChange={setPaginationPage} />
            <p className="text-sm text-muted-foreground text-center">Current page: {paginationPage}</p>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Empty State */}
      <Section title="Empty State">
        <Subsection label="No data">
          <IntuiEmptyState
            variant="no-data"
            title="No documents yet"
            description="Upload your first document to get started."
            actions={<IntuiButton size="sm"><Plus className="mr-2 h-4 w-4" />Upload Document</IntuiButton>}
            className="w-[400px]"
          />
        </Subsection>
        <Subsection label="No results">
          <IntuiEmptyState
            variant="no-results"
            title="No results found"
            description="Try adjusting your search or filters."
            className="w-[400px]"
          />
        </Subsection>
        <Subsection label="Error">
          <IntuiEmptyState
            variant="error"
            title="Something went wrong"
            description="We couldn't load the data. Please try again."
            actions={<IntuiButton variant="outline" size="sm">Retry</IntuiButton>}
            className="w-[400px]"
          />
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Direction Provider */}
      <Section title="Direction Provider">
        <Subsection label="LTR (default)">
          <IntuiDirectionProvider dir="ltr">
            <div className="w-[300px] rounded-md border p-4 space-y-1">
              <p className="text-sm font-semibold">Left-to-Right</p>
              <p className="text-sm text-muted-foreground">This content flows left to right, used for English and similar languages.</p>
            </div>
          </IntuiDirectionProvider>
        </Subsection>
        <Subsection label="RTL">
          <IntuiDirectionProvider dir="rtl">
            <div className="w-[300px] rounded-md border p-4 space-y-1">
              <p className="text-sm font-semibold">Right-to-Left</p>
              <p className="text-sm text-muted-foreground">This content flows right to left, used for Arabic, Hebrew, etc.</p>
            </div>
          </IntuiDirectionProvider>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Item & ItemGroup */}
      <Section title="Item & ItemGroup">
        <Subsection label="Variants">
          <div className="w-[400px] space-y-2">
            <IntuiItem title="Default variant" description="No border, no background" variant="default" media={<FileText className="h-5 w-5" />} />
            <IntuiItem title="Outline variant" description="Border, transparent background" variant="outline" media={<Globe className="h-5 w-5" />} />
            <IntuiItem title="Muted variant" description="Subtle muted background" variant="muted" media={<Settings className="h-5 w-5" />} />
          </div>
        </Subsection>
        <Subsection label="Sizes">
          <div className="w-[400px] space-y-2">
            <IntuiItem title="Default size" size="default" media={<Image className="h-5 w-5" />} />
            <IntuiItem title="Small size" size="sm" media={<Music className="h-4 w-4" />} />
            <IntuiItem title="Extra small size" size="xs" media={<Video className="h-4 w-4" />} />
          </div>
        </Subsection>
        <Subsection label="Clickable with action">
          <div className="w-[400px]">
            <IntuiItem
              title="Clickable item"
              description="Click me or press Enter"
              media={<Mail className="h-5 w-5" />}
              action={<IntuiButton variant="ghost" size="sm"><ArrowRight className="h-4 w-4" /></IntuiButton>}
              onClick={() => toast.info('Item clicked!')}
            />
          </div>
        </Subsection>
        <Subsection label="ItemGroup with dividers">
          <div className="w-[400px]">
            <IntuiItemGroup>
              <IntuiItem title="Inbox" description="View your messages" media={<Mail className="h-5 w-5" />} action={<IntuiBadge>3</IntuiBadge>} />
              <IntuiItem title="Starred" description="Your bookmarked items" media={<Star className="h-5 w-5" />} />
              <IntuiItem title="Settings" description="Manage preferences" media={<Settings className="h-5 w-5" />} />
            </IntuiItemGroup>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Aspect Ratio */}
      <Section title="Aspect Ratio">
        <Subsection label="16:9">
          <div className="w-80">
            <IntuiAspectRatio ratio={16 / 9}>
              <img
                src="https://images.unsplash.com/photo-1588345921523-c2dcdb7f1dcd?w=800&dpr=2&q=80"
                alt="Landscape photo in 16:9 ratio"
                className="rounded-md object-cover w-full h-full"
              />
            </IntuiAspectRatio>
          </div>
        </Subsection>
        <Subsection label="1:1">
          <div className="w-40">
            <IntuiAspectRatio ratio={1}>
              <img
                src="https://images.unsplash.com/photo-1535025183041-0991a977e25b?w=800&dpr=2&q=80"
                alt="Nature photo in 1:1 ratio"
                className="rounded-md object-cover w-full h-full"
              />
            </IntuiAspectRatio>
          </div>
        </Subsection>
        <Subsection label="4:3">
          <div className="w-60">
            <IntuiAspectRatio ratio={4 / 3}>
              <img
                src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=800&dpr=2&q=80"
                alt="Workspace photo in 4:3 ratio"
                className="rounded-md object-cover w-full h-full"
              />
            </IntuiAspectRatio>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Accordion */}
      <Section title="Accordion">
        <Subsection label="Single mode (collapsible)">
          <IntuiAccordion collapsible className="w-[400px]" items={[
            { value: 'item-1', trigger: 'Is it accessible?', content: 'Yes. It adheres to the WAI-ARIA design pattern.' },
            { value: 'item-2', trigger: 'Is it styled?', content: 'Yes. It comes with default styles that match the other components.' },
            { value: 'item-3', trigger: 'Is it animated?', content: 'Yes. It uses CSS transitions for smooth expand/collapse.' },
          ]} />
        </Subsection>
        <Subsection label="Multiple mode">
          <IntuiAccordion multiple defaultValue={['item-1', 'item-2']} className="w-[400px]" items={[
            { value: 'item-1', trigger: 'Getting Started', content: 'Install the package and import the component.' },
            { value: 'item-2', trigger: 'Usage', content: 'Pass an array of items with trigger and content fields.' },
            { value: 'item-3', trigger: 'Disabled item', content: 'This item is disabled.', disabled: true },
          ]} />
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Alert */}
      <Section title="Alert">
        <Subsection label="Variants">
          <div className="w-[450px] space-y-3">
            <IntuiAlert variant="default" title="Information" description="This is a default informational alert." />
            <IntuiAlert variant="success" title="Success" description="Your changes have been saved successfully." />
            <IntuiAlert variant="warning" title="Warning" description="Your session will expire in 5 minutes." />
            <IntuiAlert variant="destructive" title="Error" description="There was a problem processing your request." />
          </div>
        </Subsection>
        <Subsection label="Dismissible">
          <div className="w-[450px]">
            {!alertDismissed ? (
              <IntuiAlert variant="success" title="Dismissible alert" description="Click the X to dismiss this alert." dismissible onDismiss={() => setAlertDismissed(true)} />
            ) : (
              <IntuiButton variant="outline" size="sm" onClick={() => setAlertDismissed(false)}>Show alert again</IntuiButton>
            )}
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Avatar */}
      <Section title="Avatar">
        <Subsection label="Sizes">
          <IntuiAvatar size="xs" fallback="XS" />
          <IntuiAvatar size="sm" fallback="SM" />
          <IntuiAvatar size="md" fallback="MD" />
          <IntuiAvatar size="lg" fallback="LG" />
          <IntuiAvatar size="xl" fallback="XL" />
        </Subsection>
        <Subsection label="With status indicators">
          <IntuiAvatar size="lg" fallback="ON" status="online" />
          <IntuiAvatar size="lg" fallback="OF" status="offline" />
          <IntuiAvatar size="lg" fallback="BS" status="busy" />
        </Subsection>
        <Subsection label="Avatar group">
          <IntuiAvatarGroup max={4}>
            <IntuiAvatar fallback="AB" />
            <IntuiAvatar fallback="CD" />
            <IntuiAvatar fallback="EF" />
            <IntuiAvatar fallback="GH" />
            <IntuiAvatar fallback="IJ" />
            <IntuiAvatar fallback="KL" />
          </IntuiAvatarGroup>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Breadcrumb */}
      <Section title="Breadcrumb">
        <Subsection label="Basic breadcrumb">
          <IntuiBreadcrumb items={[
            { label: 'Home', href: '#' },
            { label: 'Documents', href: '#' },
            { label: 'Current Page' },
          ]} />
        </Subsection>
        <Subsection label="With icons">
          <IntuiBreadcrumb items={[
            { label: 'Home', href: '#', icon: <Globe className="h-3.5 w-3.5" /> },
            { label: 'Projects', href: '#', icon: <FolderOpen className="h-3.5 w-3.5" /> },
            { label: 'Settings', icon: <Settings className="h-3.5 w-3.5" /> },
          ]} />
        </Subsection>
        <Subsection label="Truncated (maxItems=3)">
          <IntuiBreadcrumb maxItems={3} items={[
            { label: 'Home', href: '#' },
            { label: 'Level 1', href: '#' },
            { label: 'Level 2', href: '#' },
            { label: 'Level 3', href: '#' },
            { label: 'Current Page' },
          ]} />
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Spinner */}
      <Section title="Spinner">
        <Subsection label="Sizes">
          <IntuiSpinner size="sm" />
          <IntuiSpinner size="md" />
          <IntuiSpinner size="default" />
          <IntuiSpinner size="lg" />
          <IntuiSpinner size="xl" />
        </Subsection>
        <Subsection label="With label">
          <IntuiSpinner label="Loading data..." />
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Progress */}
      <Section title="Progress">
        <Subsection label="With label and value">
          <div className="w-[350px] space-y-4">
            <IntuiProgress value={progressValue} label="Upload progress" showValue />
            <div className="flex gap-2">
              <IntuiButton variant="outline" size="sm" onClick={() => setProgressValue(Math.max(0, progressValue - 10))}>-10%</IntuiButton>
              <IntuiButton variant="outline" size="sm" onClick={() => setProgressValue(Math.min(100, progressValue + 10))}>+10%</IntuiButton>
            </div>
          </div>
        </Subsection>
        <Subsection label="Sizes">
          <div className="w-[350px] space-y-3">
            <IntuiProgress value={60} size="sm" />
            <IntuiProgress value={60} size="default" />
            <IntuiProgress value={60} size="lg" />
          </div>
        </Subsection>
        <Subsection label="Indeterminate">
          <div className="w-[350px]"><IntuiProgress indeterminate /></div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Kbd */}
      <Section title="Kbd">
        <Subsection label="Single keys">
          <IntuiKbd>Esc</IntuiKbd>
          <IntuiKbd>Enter</IntuiKbd>
          <IntuiKbd>Tab</IntuiKbd>
          <IntuiKbd>Space</IntuiKbd>
        </Subsection>
        <Subsection label="Key groups (shortcuts)">
          <IntuiKbdGroup keys={['Cmd', 'K']} />
          <IntuiKbdGroup keys={['Ctrl', 'Shift', 'P']} />
          <IntuiKbdGroup keys={['Alt', 'F4']} />
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Slider */}
      <Section title="Slider">
        <Subsection label="Range slider">
          <div className="w-[350px] space-y-2"><IntuiSlider min={0} max={1000} step={10} value={sliderValue} onValueChange={setSliderValue} /><p className="text-sm text-muted-foreground text-center">${sliderValue[0]} — ${sliderValue[1]}</p></div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Command (Command Palette) */}
      <Section title="Command (Command Palette)">
        <Subsection label="With groups, icons & shortcuts">
          <IntuiCommand className="max-w-[384px]">
            <IntuiCommandInput placeholder="Type a command or search" />
            <IntuiCommandList>
              <IntuiCommandEmpty>No results found.</IntuiCommandEmpty>
              <IntuiCommandGroup heading="Suggestions">
                <IntuiCommandItem icon={<Calendar />}>Calendar</IntuiCommandItem>
                <IntuiCommandItem icon={<Smile />}>Search Emoji</IntuiCommandItem>
                <IntuiCommandItem icon={<Calculator />}>Calculator</IntuiCommandItem>
              </IntuiCommandGroup>
              <IntuiCommandSeparator />
              <IntuiCommandGroup heading="Settings">
                <IntuiCommandItem icon={<User />} shortcut="⌘P">Profile</IntuiCommandItem>
                <IntuiCommandItem icon={<CreditCard />} shortcut="⌘B">Billing</IntuiCommandItem>
                <IntuiCommandItem icon={<Settings />} shortcut="⌘S">Settings</IntuiCommandItem>
              </IntuiCommandGroup>
            </IntuiCommandList>
          </IntuiCommand>
        </Subsection>

        <Subsection label="Empty state (type something with no match)">
          <IntuiCommand className="max-w-[384px]">
            <IntuiCommandInput placeholder="Type a command or search" />
            <IntuiCommandList>
              <IntuiCommandEmpty>No results found.</IntuiCommandEmpty>
            </IntuiCommandList>
          </IntuiCommand>
        </Subsection>

        <Subsection label="Simple without icons">
          <IntuiCommand className="max-w-[384px]">
            <IntuiCommandInput placeholder="Type a command or search" />
            <IntuiCommandList>
              <IntuiCommandEmpty>No results found.</IntuiCommandEmpty>
              <IntuiCommandGroup heading="Suggestions">
                <IntuiCommandItem>Calendar</IntuiCommandItem>
                <IntuiCommandItem>Search Emoji</IntuiCommandItem>
                <IntuiCommandItem>Calculator</IntuiCommandItem>
              </IntuiCommandGroup>
            </IntuiCommandList>
          </IntuiCommand>
        </Subsection>

        <Subsection label="Single group with shortcuts">
          <IntuiCommand className="max-w-[384px]">
            <IntuiCommandInput placeholder="Type a command or search" />
            <IntuiCommandList>
              <IntuiCommandEmpty>No results found.</IntuiCommandEmpty>
              <IntuiCommandGroup heading="Settings">
                <IntuiCommandItem icon={<User />} shortcut="⌘P">Profile</IntuiCommandItem>
                <IntuiCommandItem icon={<CreditCard />} shortcut="⌘B">Billing</IntuiCommandItem>
                <IntuiCommandItem icon={<Settings />} shortcut="⌘S">Settings</IntuiCommandItem>
              </IntuiCommandGroup>
            </IntuiCommandList>
          </IntuiCommand>
        </Subsection>

        <Subsection label="Navigation + Actions">
          <IntuiCommand className="max-w-[384px]">
            <IntuiCommandInput placeholder="Type a command or search" />
            <IntuiCommandList>
              <IntuiCommandEmpty>No results found.</IntuiCommandEmpty>
              <IntuiCommandGroup heading="Suggestions">
                <IntuiCommandItem icon={<Home />} shortcut="⌘H">Home</IntuiCommandItem>
                <IntuiCommandItem icon={<Inbox />} shortcut="⌘I">Inbox</IntuiCommandItem>
                <IntuiCommandItem icon={<FileText />} shortcut="⌘D">Documents</IntuiCommandItem>
                <IntuiCommandItem icon={<FolderOpen />} shortcut="⌘F">Folders</IntuiCommandItem>
              </IntuiCommandGroup>
              <IntuiCommandSeparator />
              <IntuiCommandGroup heading="Actions">
                <IntuiCommandItem icon={<Plus />} shortcut="⌘N">New File</IntuiCommandItem>
                <IntuiCommandItem icon={<FolderPlus />} shortcut="⇧⌘N">New Folder</IntuiCommandItem>
                <IntuiCommandItem icon={<Copy />} shortcut="⌘C">Copy</IntuiCommandItem>
              </IntuiCommandGroup>
            </IntuiCommandList>
          </IntuiCommand>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Carousel */}
      <Section title="Carousel">
        <Subsection label="Single slide with counter">
          <CarouselWithCounter />
        </Subsection>

        <Subsection label="2-up horizontal">
          <IntuiCarousel className="w-full max-w-sm">
            <IntuiCarouselContent>
              {Array.from({ length: 5 }).map((_, i) => (
                <IntuiCarouselItem key={i} slidesPerView={2}>
                  <div className="p-1">
                    <div className="flex aspect-square items-center justify-center rounded-lg border bg-card">
                      <span className="text-xl font-semibold">{i + 1}</span>
                    </div>
                  </div>
                </IntuiCarouselItem>
              ))}
            </IntuiCarouselContent>
            <IntuiCarouselPrevious />
            <IntuiCarouselNext />
          </IntuiCarousel>
        </Subsection>

        <Subsection label="3-up horizontal">
          <IntuiCarousel className="w-full max-w-sm">
            <IntuiCarouselContent>
              {Array.from({ length: 5 }).map((_, i) => (
                <IntuiCarouselItem key={i} slidesPerView={3}>
                  <div className="p-1">
                    <div className="flex aspect-square items-center justify-center rounded-lg border bg-card">
                      <span className="text-xl font-semibold">{i + 1}</span>
                    </div>
                  </div>
                </IntuiCarouselItem>
              ))}
            </IntuiCarouselContent>
            <IntuiCarouselPrevious />
            <IntuiCarouselNext />
          </IntuiCarousel>
        </Subsection>

        <Subsection label="4-up horizontal">
          <IntuiCarousel className="w-full max-w-sm">
            <IntuiCarouselContent>
              {Array.from({ length: 8 }).map((_, i) => (
                <IntuiCarouselItem key={i} slidesPerView={4}>
                  <div className="p-1">
                    <div className="flex aspect-square items-center justify-center rounded-lg border bg-card">
                      <span className="text-xl font-semibold">{i + 1}</span>
                    </div>
                  </div>
                </IntuiCarouselItem>
              ))}
            </IntuiCarouselContent>
            <IntuiCarouselPrevious />
            <IntuiCarouselNext />
          </IntuiCarousel>
        </Subsection>

        <Subsection label="Vertical">
          <IntuiCarousel orientation="vertical" className="w-full max-w-xs">
            <IntuiCarouselContent className="-mt-1 h-[200px]">
              {Array.from({ length: 5 }).map((_, i) => (
                <IntuiCarouselItem key={i} slidesPerView={2} className="pt-1">
                  <div className="p-1">
                    <div className="flex h-20 items-center justify-center rounded-lg border bg-card">
                      <span className="text-xl font-semibold">{i + 1}</span>
                    </div>
                  </div>
                </IntuiCarouselItem>
              ))}
            </IntuiCarouselContent>
            <IntuiCarouselPrevious />
            <IntuiCarouselNext />
          </IntuiCarousel>
        </Subsection>

        <Subsection label="Large single slide">
          <IntuiCarousel className="w-full max-w-lg">
            <IntuiCarouselContent>
              {Array.from({ length: 5 }).map((_, i) => (
                <IntuiCarouselItem key={i}>
                  <div className="p-1">
                    <div className="flex aspect-[16/9] items-center justify-center rounded-lg border bg-card">
                      <span className="text-3xl font-semibold">{i + 1}</span>
                    </div>
                  </div>
                </IntuiCarouselItem>
              ))}
            </IntuiCarouselContent>
            <IntuiCarouselPrevious />
            <IntuiCarouselNext />
          </IntuiCarousel>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Area Charts */}
      <Section title="Area Chart">
        <Subsection label="All variants (in chart cards)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-[800px]">
            <IntuiChartCard
              title="Area Chart"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Linear"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                curveType="linear"
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Step"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                curveType="step"
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Stacked"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_MULTI_CONFIG}
                stacked
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Stacked Expanded"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_MULTI_CONFIG}
                stackedExpanded
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Legend"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_MULTI_CONFIG}
                showLegend
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Gradient"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                gradient
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Axes"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                showYAxis
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Linear + Gradient"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                curveType="linear"
                gradient
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Step + Gradient"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                curveType="step"
                gradient
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Stacked + Gradient"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_MULTI_CONFIG}
                stacked
                gradient
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Stacked Expanded + Gradient"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_MULTI_CONFIG}
                stackedExpanded
                gradient
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Area Chart - Icons"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiAreaChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_ICON_CONFIG}
                showLegend
                className="h-[200px]"
              />
            </IntuiChartCard>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Bar Charts */}
      <Section title="Bar Chart">
        <Subsection label="All variants">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-[800px]">
            <IntuiChartCard
              title="Bar Chart"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiBarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Bar Chart - Horizontal"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiBarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                layout="horizontal"
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Bar Chart - Mixed"
              description="Browser visitors breakdown"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiBarChart
                data={BAR_CHART_MIXED_DATA}
                config={BAR_CHART_MIXED_CONFIG}
                dataKeys={['visitors']}
                categoryKey="browser"
                layout="horizontal"
                useFillFromData
                fillKey="fill"
                showGrid={false}
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Bar Chart - Custom Label"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiBarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                layout="horizontal"
                showLabels
                labelPosition="right"
                showGrid={false}
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Bar Chart - Active"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiBarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                activeIndex={2}
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Bar Chart - Stacked"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiBarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_MULTI_CONFIG}
                stacked
                showLegend
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Bar Chart - Label"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiBarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                showLabels
                labelPosition="top"
                showGrid={false}
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Bar Chart - Negative"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiBarChart
                data={BAR_CHART_NEGATIVE_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Bar Chart - Multiple"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiBarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_MULTI_CONFIG}
                showLegend
                className="h-[200px]"
              />
            </IntuiChartCard>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Line Charts */}
      <Section title="Line Chart">
        <Subsection label="All variants">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-[800px]">
            <IntuiChartCard
              title="Line Chart"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiLineChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Line Chart - Linear"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiLineChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                curveType="linear"
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Line Chart - Step"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiLineChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                curveType="step"
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Line Chart - Multiple"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiLineChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_MULTI_CONFIG}
                showLegend
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Line Chart - Dots"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiLineChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                showDots
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Line Chart - Dots Colors"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiLineChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                showDots
                dotRadius={6}
                className="h-[200px]"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Line Chart - Label"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiLineChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                showDots
                showLabels
                className="h-[200px]"
              />
            </IntuiChartCard>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Pie Charts */}
      <Section title="Pie Chart">
        <Subsection label="All variants">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-[800px]">
            <IntuiChartCard
              title="Pie Chart"
              description="Browser share breakdown"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiPieChart
                data={PIE_CHART_DATA}
                config={PIE_CHART_CONFIG}
                showLegend
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Pie Chart - Label"
              description="Browser share with value labels"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiPieChart
                data={PIE_CHART_DATA}
                config={PIE_CHART_CONFIG}
                showLabels
                showLegend
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Donut Chart"
              description="Browser share as donut with center total"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiPieChart
                data={PIE_CHART_DATA}
                config={PIE_CHART_CONFIG}
                variant="donut"
                centerValue={1125}
                centerLabel="Visitors"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Donut Chart - Active"
              description="Donut with highlighted sector"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiPieChart
                data={PIE_CHART_DATA}
                config={PIE_CHART_CONFIG}
                variant="donut-active"
                activeIndex={0}
                centerValue={1125}
                centerLabel="Visitors"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Donut Chart - Stacked"
              description="Concentric donut rings"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiPieChart
                data={PIE_CHART_DATA}
                config={PIE_CHART_CONFIG}
                variant="donut-stacked"
                stackedData={PIE_CHART_DATA}
              />
            </IntuiChartCard>

          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Radar Charts */}
      <Section title="Radar Chart">
        <Subsection label="All variants">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-[800px]">
            <IntuiChartCard
              title="Radar Chart"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radar Chart - Dots"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                showDots
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radar Chart - Multiple"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_MULTI_CONFIG}
                showLegend
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radar Chart - Lines Only"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_MULTI_CONFIG}
                showFill={false}
                showLegend
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radar Chart - Grid Filled"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                gridFill
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radar Chart - Grid Circle"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                gridType="circle"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radar Chart - No Lines"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                showGrid={false}
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radar Chart - Grid Circle Filled"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                gridType="circle"
                gridFill
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radar Chart - Grid Custom"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadarChart
                data={AREA_CHART_DATA}
                config={AREA_CHART_SINGLE_CONFIG}
                dataKeys={['desktop']}
                showRadiusAxis
              />
            </IntuiChartCard>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Radial Charts */}
      <Section title="Radial Chart">
        <Subsection label="All variants">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-[800px]">
            <IntuiChartCard
              title="Radial Chart"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadialChart data={PIE_CHART_DATA} config={PIE_CHART_CONFIG} />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radial Chart - Label"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadialChart data={PIE_CHART_DATA} config={PIE_CHART_CONFIG} variant="label" />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radial Chart - Grid"
              description="Showing total visitors for the last 6 months"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadialChart data={PIE_CHART_DATA} config={PIE_CHART_CONFIG} variant="grid" />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radial Chart - Text"
              description="Single metric display"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadialChart
                data={RADIAL_SINGLE_DATA}
                config={PIE_CHART_CONFIG}
                variant="text"
                centerValue={200}
                centerLabel="Visitors"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radial Chart - Shape"
              description="Half-arc metric display"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadialChart
                data={RADIAL_SINGLE_DATA}
                config={PIE_CHART_CONFIG}
                variant="shape"
                centerValue={200}
                centerLabel="Visitors"
              />
            </IntuiChartCard>

            <IntuiChartCard
              title="Radial Chart - Stacked"
              description="Stacked arcs with total"
              trendText="Trending up by 5.2% this month"
              dateRange="January - June 2024"
            >
              <IntuiRadialChart
                data={PIE_CHART_DATA}
                config={PIE_CHART_CONFIG}
                variant="stacked"
                centerValue={200}
                centerLabel="Visitors"
              />
            </IntuiChartCard>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Chart Tooltip Demos */}
      <Section title="Chart Tooltip">
        <Subsection label="Tooltip indicator variants on bar charts">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full max-w-[800px]">
            <IntuiChartCard title="Tooltip - Default" description="Dot indicator (default)">
              <ChartContainer config={AREA_CHART_MULTI_CONFIG} className="h-[200px]">
                <BarChart data={AREA_CHART_DATA}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v) => v.slice(0, 3)} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="desktop" fill="var(--color-desktop)" radius={4} />
                  <Bar dataKey="mobile" fill="var(--color-mobile)" radius={4} />
                </BarChart>
              </ChartContainer>
            </IntuiChartCard>

            <IntuiChartCard title="Tooltip - Line Indicator" description="Line indicator style">
              <ChartContainer config={AREA_CHART_MULTI_CONFIG} className="h-[200px]">
                <BarChart data={AREA_CHART_DATA}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v) => v.slice(0, 3)} />
                  <ChartTooltip content={<ChartTooltipContent indicator="line" />} />
                  <Bar dataKey="desktop" fill="var(--color-desktop)" radius={4} />
                  <Bar dataKey="mobile" fill="var(--color-mobile)" radius={4} />
                </BarChart>
              </ChartContainer>
            </IntuiChartCard>

            <IntuiChartCard title="Tooltip - No Indicator" description="Hidden indicator">
              <ChartContainer config={AREA_CHART_MULTI_CONFIG} className="h-[200px]">
                <BarChart data={AREA_CHART_DATA}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v) => v.slice(0, 3)} />
                  <ChartTooltip content={<ChartTooltipContent hideIndicator />} />
                  <Bar dataKey="desktop" fill="var(--color-desktop)" radius={4} />
                  <Bar dataKey="mobile" fill="var(--color-mobile)" radius={4} />
                </BarChart>
              </ChartContainer>
            </IntuiChartCard>

            <IntuiChartCard title="Tooltip - Custom Label" description="Custom label text">
              <ChartContainer config={AREA_CHART_MULTI_CONFIG} className="h-[200px]">
                <BarChart data={AREA_CHART_DATA}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v) => v.slice(0, 3)} />
                  <ChartTooltip content={<ChartTooltipContent labelKey="month" />} />
                  <Bar dataKey="desktop" fill="var(--color-desktop)" radius={4} />
                  <Bar dataKey="mobile" fill="var(--color-mobile)" radius={4} />
                </BarChart>
              </ChartContainer>
            </IntuiChartCard>

            <IntuiChartCard title="Tooltip - Label Formatter" description="Formatted label">
              <ChartContainer config={AREA_CHART_MULTI_CONFIG} className="h-[200px]">
                <BarChart data={AREA_CHART_DATA}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v) => v.slice(0, 3)} />
                  <ChartTooltip content={<ChartTooltipContent labelFormatter={(value) => `Month: ${value}`} />} />
                  <Bar dataKey="desktop" fill="var(--color-desktop)" radius={4} />
                  <Bar dataKey="mobile" fill="var(--color-mobile)" radius={4} />
                </BarChart>
              </ChartContainer>
            </IntuiChartCard>

            <IntuiChartCard title="Tooltip - No Label" description="Hidden label">
              <ChartContainer config={AREA_CHART_MULTI_CONFIG} className="h-[200px]">
                <BarChart data={AREA_CHART_DATA}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v) => v.slice(0, 3)} />
                  <ChartTooltip content={<ChartTooltipContent hideLabel />} />
                  <Bar dataKey="desktop" fill="var(--color-desktop)" radius={4} />
                  <Bar dataKey="mobile" fill="var(--color-mobile)" radius={4} />
                </BarChart>
              </ChartContainer>
            </IntuiChartCard>

            <IntuiChartCard title="Tooltip - Formatter" description="Custom value formatter">
              <ChartContainer config={AREA_CHART_MULTI_CONFIG} className="h-[200px]">
                <BarChart data={AREA_CHART_DATA}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v) => v.slice(0, 3)} />
                  <ChartTooltip content={<ChartTooltipContent formatter={(value, name) => <span className="font-mono">{name}: {Number(value).toLocaleString()} visits</span>} />} />
                  <Bar dataKey="desktop" fill="var(--color-desktop)" radius={4} />
                  <Bar dataKey="mobile" fill="var(--color-mobile)" radius={4} />
                </BarChart>
              </ChartContainer>
            </IntuiChartCard>

            <IntuiChartCard title="Tooltip - Icons" description="With icon legend items">
              <ChartContainer config={AREA_CHART_ICON_CONFIG} className="h-[200px]">
                <BarChart data={AREA_CHART_DATA}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v) => v.slice(0, 3)} />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="desktop" fill="var(--color-desktop)" radius={4} />
                  <Bar dataKey="mobile" fill="var(--color-mobile)" radius={4} />
                </BarChart>
              </ChartContainer>
            </IntuiChartCard>

            <IntuiChartCard title="Tooltip - Advanced" description="Dashed indicator style">
              <ChartContainer config={AREA_CHART_MULTI_CONFIG} className="h-[200px]">
                <BarChart data={AREA_CHART_DATA}>
                  <CartesianGrid vertical={false} />
                  <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} tickFormatter={(v) => v.slice(0, 3)} />
                  <ChartTooltip content={<ChartTooltipContent indicator="dashed" />} />
                  <Bar dataKey="desktop" fill="var(--color-desktop)" radius={4} />
                  <Bar dataKey="mobile" fill="var(--color-mobile)" radius={4} />
                </BarChart>
              </ChartContainer>
            </IntuiChartCard>
          </div>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Context Menu */}
      <Section title="Context Menu">
        <Subsection label="Kitchen sink (right-click)">
          <IntuiContextMenu>
            <IntuiContextMenuTrigger>
              <div className="flex h-32 w-64 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Right click here
              </div>
            </IntuiContextMenuTrigger>
            <IntuiContextMenuContent>
              <IntuiContextMenuItem shortcut="⌘[">Back</IntuiContextMenuItem>
              <IntuiContextMenuItem disabled>Forward</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⌘R">Reload</IntuiContextMenuItem>
              <IntuiContextMenuSub>
                <IntuiContextMenuSubTrigger>More Tools</IntuiContextMenuSubTrigger>
                <IntuiContextMenuSubContent>
                  <IntuiContextMenuItem>Save Page...</IntuiContextMenuItem>
                  <IntuiContextMenuItem>Create Shortcut...</IntuiContextMenuItem>
                  <IntuiContextMenuItem>Developer Tools</IntuiContextMenuItem>
                </IntuiContextMenuSubContent>
              </IntuiContextMenuSub>
              <IntuiContextMenuSeparator />
              <IntuiContextMenuCheckboxItem checked>
                Show Bookmarks
              </IntuiContextMenuCheckboxItem>
              <IntuiContextMenuCheckboxItem>Show Full URLs</IntuiContextMenuCheckboxItem>
              <IntuiContextMenuSeparator />
              <IntuiContextMenuLabel>People</IntuiContextMenuLabel>
              <IntuiContextMenuRadioGroup value="pedro">
                <IntuiContextMenuRadioItem value="pedro">Pedro Duarte</IntuiContextMenuRadioItem>
                <IntuiContextMenuRadioItem value="colm">Colm Tuite</IntuiContextMenuRadioItem>
              </IntuiContextMenuRadioGroup>
            </IntuiContextMenuContent>
          </IntuiContextMenu>
        </Subsection>

        <Subsection label="Minimal">
          <IntuiContextMenu>
            <IntuiContextMenuTrigger>
              <div className="flex h-20 w-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Right click
              </div>
            </IntuiContextMenuTrigger>
            <IntuiContextMenuContent>
              <IntuiContextMenuItem>Back</IntuiContextMenuItem>
              <IntuiContextMenuItem>Forward</IntuiContextMenuItem>
              <IntuiContextMenuItem>Reload</IntuiContextMenuItem>
            </IntuiContextMenuContent>
          </IntuiContextMenu>
        </Subsection>

        <Subsection label="Sub-menu with destructive">
          <IntuiContextMenu>
            <IntuiContextMenuTrigger>
              <div className="flex h-20 w-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Right click
              </div>
            </IntuiContextMenuTrigger>
            <IntuiContextMenuContent>
              <IntuiContextMenuItem shortcut="⌘C">Copy</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⌘X">Cut</IntuiContextMenuItem>
              <IntuiContextMenuSub>
                <IntuiContextMenuSubTrigger>More Tools</IntuiContextMenuSubTrigger>
                <IntuiContextMenuSubContent>
                  <IntuiContextMenuItem>Save Page...</IntuiContextMenuItem>
                  <IntuiContextMenuItem>Create Shortcut...</IntuiContextMenuItem>
                  <IntuiContextMenuItem>Name Window...</IntuiContextMenuItem>
                  <IntuiContextMenuItem>Developer Tools</IntuiContextMenuItem>
                  <IntuiContextMenuSeparator />
                  <IntuiContextMenuItem variant="destructive">Delete</IntuiContextMenuItem>
                </IntuiContextMenuSubContent>
              </IntuiContextMenuSub>
            </IntuiContextMenuContent>
          </IntuiContextMenu>
        </Subsection>

        <Subsection label="With shortcuts">
          <IntuiContextMenu>
            <IntuiContextMenuTrigger>
              <div className="flex h-20 w-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Right click
              </div>
            </IntuiContextMenuTrigger>
            <IntuiContextMenuContent>
              <IntuiContextMenuItem shortcut="⌘[">Back</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⌘]">Forward</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⌘R">Reload</IntuiContextMenuItem>
              <IntuiContextMenuSeparator />
              <IntuiContextMenuItem shortcut="⌘S">Save</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⇧⌘S">Save As...</IntuiContextMenuItem>
            </IntuiContextMenuContent>
          </IntuiContextMenu>
        </Subsection>

        <Subsection label="Labels & groups">
          <IntuiContextMenu>
            <IntuiContextMenuTrigger>
              <div className="flex h-20 w-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Right click
              </div>
            </IntuiContextMenuTrigger>
            <IntuiContextMenuContent>
              <IntuiContextMenuLabel>File</IntuiContextMenuLabel>
              <IntuiContextMenuItem shortcut="⌘N">New File</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⌘O">Open File</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⌘S">Save</IntuiContextMenuItem>
              <IntuiContextMenuSeparator />
              <IntuiContextMenuLabel>Edit</IntuiContextMenuLabel>
              <IntuiContextMenuItem shortcut="⌘Z">Undo</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⇧⌘Z">Redo</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⌘X">Cut</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⌘C">Copy</IntuiContextMenuItem>
              <IntuiContextMenuItem shortcut="⌘V">Paste</IntuiContextMenuItem>
              <IntuiContextMenuSeparator />
              <IntuiContextMenuItem variant="destructive" shortcut="⌫">Delete</IntuiContextMenuItem>
            </IntuiContextMenuContent>
          </IntuiContextMenu>
        </Subsection>

        <Subsection label="With icons">
          <IntuiContextMenu>
            <IntuiContextMenuTrigger>
              <div className="flex h-20 w-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Right click
              </div>
            </IntuiContextMenuTrigger>
            <IntuiContextMenuContent>
              <IntuiContextMenuItem icon={<Copy />}>Copy</IntuiContextMenuItem>
              <IntuiContextMenuItem icon={<Scissors />}>Cut</IntuiContextMenuItem>
              <IntuiContextMenuItem icon={<Clipboard />}>Paste</IntuiContextMenuItem>
              <IntuiContextMenuSeparator />
              <IntuiContextMenuItem variant="destructive" icon={<Trash2 />}>Delete</IntuiContextMenuItem>
            </IntuiContextMenuContent>
          </IntuiContextMenu>
        </Subsection>

        <Subsection label="Checkbox items">
          <IntuiContextMenu>
            <IntuiContextMenuTrigger>
              <div className="flex h-20 w-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Right click
              </div>
            </IntuiContextMenuTrigger>
            <IntuiContextMenuContent>
              <IntuiContextMenuCheckboxItem checked>Show Bookmarks Bar</IntuiContextMenuCheckboxItem>
              <IntuiContextMenuCheckboxItem>Show Full URLs</IntuiContextMenuCheckboxItem>
              <IntuiContextMenuCheckboxItem>Show Developer Tools</IntuiContextMenuCheckboxItem>
            </IntuiContextMenuContent>
          </IntuiContextMenu>
        </Subsection>

        <Subsection label="Radio groups">
          <IntuiContextMenu>
            <IntuiContextMenuTrigger>
              <div className="flex h-20 w-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Right click
              </div>
            </IntuiContextMenuTrigger>
            <IntuiContextMenuContent>
              <IntuiContextMenuLabel>People</IntuiContextMenuLabel>
              <IntuiContextMenuRadioGroup value="pedro">
                <IntuiContextMenuRadioItem value="pedro">Pedro Duarte</IntuiContextMenuRadioItem>
                <IntuiContextMenuRadioItem value="colm">Colm Tuite</IntuiContextMenuRadioItem>
              </IntuiContextMenuRadioGroup>
              <IntuiContextMenuSeparator />
              <IntuiContextMenuLabel>Theme</IntuiContextMenuLabel>
              <IntuiContextMenuRadioGroup value="light">
                <IntuiContextMenuRadioItem value="light">Light</IntuiContextMenuRadioItem>
                <IntuiContextMenuRadioItem value="dark">Dark</IntuiContextMenuRadioItem>
                <IntuiContextMenuRadioItem value="system">System</IntuiContextMenuRadioItem>
              </IntuiContextMenuRadioGroup>
            </IntuiContextMenuContent>
          </IntuiContextMenu>
        </Subsection>

        <Subsection label="Icon + destructive">
          <IntuiContextMenu>
            <IntuiContextMenuTrigger>
              <div className="flex h-20 w-48 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
                Right click
              </div>
            </IntuiContextMenuTrigger>
            <IntuiContextMenuContent>
              <IntuiContextMenuItem icon={<Pencil />}>Edit</IntuiContextMenuItem>
              <IntuiContextMenuItem icon={<Share2 />}>Share</IntuiContextMenuItem>
              <IntuiContextMenuSeparator />
              <IntuiContextMenuItem variant="destructive" icon={<Trash2 />}>Delete</IntuiContextMenuItem>
            </IntuiContextMenuContent>
          </IntuiContextMenu>
        </Subsection>
      </Section>

      <IntuiSeparator />

      {/* Date Picker */}
      <Section title="Date Picker">
        <Subsection label="Trigger types">
          <div className="flex flex-wrap gap-4">
            <div className="w-[212px] space-y-1">
              <p className="text-xs text-muted-foreground">Icon Left</p>
              <IntuiDatePicker type="icon-left" />
            </div>
            <div className="w-[212px] space-y-1">
              <p className="text-xs text-muted-foreground">With Input</p>
              <IntuiDatePicker type="with-input" />
            </div>
            <div className="w-[212px] space-y-1">
              <p className="text-xs text-muted-foreground">Icon Right</p>
              <IntuiDatePicker type="icon-right" />
            </div>
          </div>
        </Subsection>

        <Subsection label="Basic (single date — Icon Left)">
          <div className="w-[212px]">
            <IntuiDatePicker value={new Date(2025, 0, 10)} />
          </div>
        </Subsection>

        <Subsection label="Date Range Picker">
          <div className="w-[300px]">
            <IntuiDateRangePicker
              value={{ from: new Date(2025, 0, 12), to: new Date(2025, 1, 8) }}
            />
          </div>
        </Subsection>

        <Subsection label="Date of Birth (Icon Right)">
          <div className="w-[212px]">
            <IntuiDatePicker
              type="icon-right"
              placeholder="Select date"
              calendarProps={{ captionLayout: 'dropdown' }}
            />
          </div>
        </Subsection>

        <Subsection label="Subscription Date (With Input)">
          <div className="w-[212px]">
            <IntuiDatePicker type="with-input" />
          </div>
        </Subsection>

        <Subsection label="Date + Time Picker">
          <IntuiDateTimePicker
            datePlaceholder="Select date"
            time="10:30:00"
          />
        </Subsection>

        <Subsection label="Time Picker (standalone)">
          <div className="flex gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Default</p>
              <IntuiTimePicker value="10:30:00" />
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Disabled</p>
              <IntuiTimePicker value="10:30:00" disabled />
            </div>
          </div>
        </Subsection>

        <Subsection label="Disabled states">
          <div className="flex flex-wrap gap-4">
            <div className="w-[212px]">
              <IntuiDatePicker disabled type="icon-left" />
            </div>
            <div className="w-[212px]">
              <IntuiDatePicker disabled type="with-input" />
            </div>
            <div className="w-[212px]">
              <IntuiDatePicker disabled type="icon-right" />
            </div>
          </div>
        </Subsection>
      </Section>
    </div>
  )
}
